import React, { useState, useEffect } from 'react';
import { 
  Key, Eye, EyeOff, CheckCircle, AlertTriangle, HelpCircle, 
  ExternalLink, RefreshCw, Trash2, ShieldCheck, Sparkles,
  Users, UserCheck, UserX, Lock, Unlock, Search, Mail, Calendar,
  Send, BarChart3, Activity, FileText, Clock, TrendingUp, Download, Copy, UserPlus, Globe,
  AlertCircle, CheckCircle2
} from 'lucide-react';
import agentsConfig from '../agentsConfig.json';

interface SettingsViewProps {
  language: 'uk' | 'en' | 'pl';
}

interface UserProfileData {
  email: string;
  displayName: string;
  avatar?: string;
  status: 'pending' | 'approved' | 'rejected';
  role: 'admin' | 'user';
  twoFactorEnabled: boolean;
  has2FASecret: boolean;
  createdAt: string;
  unlimitedAll?: boolean;
  unlimitedAgents?: string[];
}

export default function SettingsView({ language }: SettingsViewProps) {
  // API Key States
  const [apiKey, setApiKey] = useState('');
  const [savedKey, setSavedKey] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState<{
    status: 'idle' | 'success' | 'error';
    message: string;
  }>({ status: 'idle', message: '' });

  // User Management States
  const [activeSubTab, setActiveSubTab] = useState<'apiKey' | 'users' | 'pricing' | 'satellite' | 'ncuvkz' | 'telegram' | 'email' | 'emailMonitors' | 'agentStats' | 'publicApis'>('apiKey');

  // NCUVKZ & NIS-ECO Gateway States
  const [copernicusMirrorUrl, setCopernicusMirrorUrl] = useState('https://copernicus.spacecenter.gov.ua/api/stac');
  const [copernicusApiKey, setCopernicusApiKey] = useState('');
  const [copernicusS3Bucket, setCopernicusS3Bucket] = useState('s3://copernicus-mirror-ua/sentinel/');
  const [ntripCasterHost, setNtripCasterHost] = useState('ntrip.spacecenter.gov.ua');
  const [ntripCasterPort, setNtripCasterPort] = useState('2101');
  const [ntripUser, setNtripUser] = useState('nis_eco_client');
  const [ntripPassword, setNtripPassword] = useState('');
  const [ntripMountpoints, setNtripMountpoints] = useState('DNIP00UKR0,ZAPO00UKR0,KHER00UKR0,UZHL00UKR0,KYIV00UKR0');
  const [sknzuRinexFtp, setSknzuRinexFtp] = useState('ftp://rinex.spacecenter.gov.ua/pub/rinex/');
  const [seedlinkHost, setSeedlinkHost] = useState('seedlink.gcsk.gov.ua');
  const [seedlinkPort, setSeedlinkPort] = useState('18000');
  const [seedlinkChannels, setSeedlinkChannels] = useState('PS45.MALYN.HHZ,BALT.HHZ,UZHG.HHZ');
  const [gcskEventsApi, setGcskEventsApi] = useState('https://events.gcsk.gov.ua/api/v1/bulletins');
  const [ncuvkzDaemonEnabled, setNcuvkzDaemonEnabled] = useState(true);
  const [ncuvkzDaemonInterval, setNcuvkzDaemonInterval] = useState('15');
  const [ncuvkzOperatorChatId, setNcuvkzOperatorChatId] = useState('1421638496');
  const [isLoadingNcuvkz, setIsLoadingNcuvkz] = useState(false);
  const [isSavingNcuvkz, setIsSavingNcuvkz] = useState(false);
  const [isTestingNcuvkz, setIsTestingNcuvkz] = useState(false);
  const [isPingingTelegram, setIsPingingTelegram] = useState(false);
  const [ncuvkzSaveResult, setNcuvkzSaveResult] = useState<{ status: 'idle' | 'success' | 'error'; message: string }>({ status: 'idle', message: '' });
  const [ncuvkzTestResult, setNcuvkzTestResult] = useState<{
    status: 'idle' | 'success' | 'warning' | 'error';
    message: string;
    serverIp?: string;
    gateways?: Array<{
      id: string;
      name: string;
      icon: string;
      status: 'ready' | 'missing_auth' | 'unreachable';
      statusBadge: string;
      stateDescription: string;
      actionRequired: string;
    }>;
    operatorChecklist?: string[];
    details?: any;
  }>({ status: 'idle', message: '' });
  const [ncuvkzPingResult, setNcuvkzPingResult] = useState<{ status: 'idle' | 'success' | 'error'; message: string }>({ status: 'idle', message: '' });

  // Email Monitors States
  const [emailMonitors, setEmailMonitors] = useState<any[]>([]);
  const [isLoadingMonitors, setIsLoadingMonitors] = useState(false);
  const [newMonitorEmail, setNewMonitorEmail] = useState('');
  const [newMonitorPassword, setNewMonitorPassword] = useState('');
  const [newMonitorProvider, setNewMonitorProvider] = useState<'ukr.net' | 'gmail'>('ukr.net');
  const [newMonitorDescription, setNewMonitorDescription] = useState('');
  const [isAddingMonitor, setIsAddingMonitor] = useState(false);
  const [isScanningMonitors, setIsScanningMonitors] = useState(false);
  const [monitorMessage, setMonitorMessage] = useState<string | null>(null);

  // Public APIs Health States
  const [publicApisStatus, setPublicApisStatus] = useState<any[]>([]);
  const [isLoadingPublicApis, setIsLoadingPublicApis] = useState(false);

  // Commercial Satellite Credentials States
  const [up42ProjectId, setUp42ProjectId] = useState('');
  const [up42ProjectSecret, setUp42ProjectSecret] = useState('');
  const [up42Env, setUp42Env] = useState<'production' | 'staging'>('production');
  const [planetApiKey, setPlanetApiKey] = useState('');
  const [maxarApiToken, setMaxarApiToken] = useState('');
  const [showUp42Secret, setShowUp42Secret] = useState(false);
  const [showPlanetKey, setShowPlanetKey] = useState(false);
  const [isLoadingSatellite, setIsLoadingSatellite] = useState(false);
  const [isSavingSatellite, setIsSavingSatellite] = useState(false);
  const [isTestingUp42, setIsTestingUp42] = useState(false);
  const [satelliteSaveResult, setSatelliteSaveResult] = useState<{ status: 'idle' | 'success' | 'error'; message: string }>({ status: 'idle', message: '' });
  const [up42TestResult, setUp42TestResult] = useState<{ status: 'idle' | 'success' | 'error'; message: string }>({ status: 'idle', message: '' });
  const [users, setUsers] = useState<UserProfileData[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [updatingUserEmail, setUpdatingUserEmail] = useState<string | null>(null);

  // Add User States
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserName, setNewUserName] = useState('');
  const [newUserUnlimited, setNewUserUnlimited] = useState(false);
  const [isAddingUser, setIsAddingUser] = useState(false);
  const [addUserSuccess, setAddUserSuccess] = useState<string | null>(null);
  const [addUserError, setAddUserError] = useState<string | null>(null);

  // Unlimited Access States
  const [unlimitedModalUser, setUnlimitedModalUser] = useState<UserProfileData | null>(null);
  const [unlimitedMode, setUnlimitedMode] = useState<'none' | 'all' | 'selected'>('none');
  const [selectedAgents, setSelectedAgents] = useState<string[]>([]);
  const [agentSearchQuery, setAgentSearchQuery] = useState('');
  const [isSavingUnlimited, setIsSavingUnlimited] = useState(false);

  // Pricing & Payments States
  const [agentSettings, setAgentSettings] = useState<any[]>([]);
  const [monobankToken, setMonobankToken] = useState('');
  const [isLoadingPricing, setIsLoadingPricing] = useState(false);
  const [isSavingToken, setIsSavingToken] = useState(false);
  const [isTestingMono, setIsTestingMono] = useState(false);
  const [monoTestResult, setMonoTestResult] = useState<{ ok: boolean; message: string; isTerminalMissing?: boolean } | null>(null);
  const [savingAgentId, setSavingAgentId] = useState<string | null>(null);

  // Telegram Settings States
  const [telegramBotToken, setTelegramBotToken] = useState('');
  const [telegramChatId, setTelegramChatId] = useState('');
  const [telegramPublicChannelId, setTelegramPublicChannelId] = useState('');
  const [marketRadarPublicChannelAutopost, setMarketRadarPublicChannelAutopost] = useState(true);
  const [lastMarketRadarPostedAt, setLastMarketRadarPostedAt] = useState<string | null>(null);
  const [isTestingPublicChannel, setIsTestingPublicChannel] = useState(false);
  const [isPublishingPublicChannel, setIsPublishingPublicChannel] = useState(false);
  const [publicChannelStatusMsg, setPublicChannelStatusMsg] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);
  const [cachedPublicDigest, setCachedPublicDigest] = useState<any | null>(null);
  const [isLoadingPublicDigest, setIsLoadingPublicDigest] = useState(false);
  const [isLoadingTelegram, setIsLoadingTelegram] = useState(false);
  const [isSavingTelegram, setIsSavingTelegram] = useState(false);

  // Email & Mailing States
  const [smtpSettings, setSmtpSettings] = useState({ host: '', port: '587', user: '', pass: '', from: '' });
  const [emailLogs, setEmailLogs] = useState('');
  const [isLoadingEmail, setIsLoadingEmail] = useState(false);
  const [isSavingSmtp, setIsSavingSmtp] = useState(false);
  const [isSendingInactivityReminder, setIsSendingInactivityReminder] = useState(false);
  const [inactivityReminderResult, setInactivityReminderResult] = useState<string | null>(null);
  const [isSendingInvitationAll, setIsSendingInvitationAll] = useState(false);
  const [invitationAllResult, setInvitationAllResult] = useState<string | null>(null);
  const [invitationCustomNote, setInvitationCustomNote] = useState('');

  // Agent Usage Statistics States
  const [usageStatsData, setUsageStatsData] = useState<{
    totals: { total_runs: number; total_runs_30d: number; total_users: number; total_agents: number };
    userAgentStats: Array<{ user_email: string; agent_id: string; total_count: number; count_30d: number; last_used_at: string }>;
    userSummary: Array<{ user_email: string; total_count: number; count_30d: number; last_used_at: string }>;
    agentSummary: Array<{ agent_id: string; total_count: number; count_30d: number; last_used_at: string }>;
    markdownContent: string;
    markdownFilePath: string;
  } | null>(null);
  const [isLoadingUsageStats, setIsLoadingUsageStats] = useState(false);
  const [statsViewMode, setStatsViewMode] = useState<'userAgent' | 'userSummary' | 'agentSummary' | 'markdown'>('userAgent');
  const [statsSearchQuery, setStatsSearchQuery] = useState('');

  // Fetch agent usage stats
  const fetchAgentUsageStats = async () => {
    setIsLoadingUsageStats(true);
    try {
      const res = await fetch('/api/admin/agent-usage-stats');
      if (res.ok) {
        const data = await res.json();
        setUsageStatsData(data);
      }
    } catch (err) {
      console.error("Failed to fetch agent usage stats", err);
    } finally {
      setIsLoadingUsageStats(false);
    }
  };

  const handleRefreshAgentStats = async () => {
    setIsLoadingUsageStats(true);
    try {
      const res = await fetch('/api/admin/agent-usage-stats/refresh', { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        setUsageStatsData(data);
      }
    } catch (err) {
      console.error("Failed to refresh agent usage stats", err);
    } finally {
      setIsLoadingUsageStats(false);
    }
  };

  // Fetch agent usage stats when switching to agentStats tab
  useEffect(() => {
    if (activeSubTab === 'agentStats') {
      fetchAgentUsageStats();
    }
  }, [activeSubTab]);

  // Load saved key from localStorage on mount
  useEffect(() => {
    let stored = localStorage.getItem('custom_gemini_api_key') || '';
    if (stored.startsWith('AQ.Ab8RN')) {
      localStorage.removeItem('custom_gemini_api_key');
      stored = '';
    }
    setSavedKey(stored);
    setApiKey(stored);
  }, []);

  // Fetch users when transitioning to user subtab
  useEffect(() => {
    if (activeSubTab === 'users') {
      fetchUsers();
    }
  }, [activeSubTab]);

  // Fetch pricing when transitioning to pricing subtab
  useEffect(() => {
    if (activeSubTab === 'pricing') {
      fetchPricingSettings();
    }
  }, [activeSubTab]);

  // Fetch telegram settings when transitioning to telegram subtab
  useEffect(() => {
    if (activeSubTab === 'telegram') {
      fetchTelegramSettings();
      fetchPublicDigest();
    }
  }, [activeSubTab]);

  // Fetch email settings and logs when transitioning to email subtab
  useEffect(() => {
    if (activeSubTab === 'email' || activeSubTab === 'emailMonitors') {
      fetchEmailSettingsAndLogs();
      fetchEmailMonitors();
    }
  }, [activeSubTab]);

  const fetchEmailMonitors = async () => {
    setIsLoadingMonitors(true);
    try {
      const res = await fetch('/api/admin/email-monitors');
      if (res.ok) {
        const data = await res.json();
        setEmailMonitors(data.monitors || []);
      }
    } catch (err) {
      console.error('Failed to fetch email monitors:', err);
    } finally {
      setIsLoadingMonitors(false);
    }
  };

  // Fetch satellite settings when transitioning to satellite subtab
  useEffect(() => {
    if (activeSubTab === 'satellite') {
      fetchSatelliteSettings();
    }
    if (activeSubTab === 'ncuvkz') {
      fetchNcuvkzSettings();
    }
  }, [activeSubTab]);

  const fetchPublicApisHealth = async () => {
    setIsLoadingPublicApis(true);
    try {
      const res = await fetch('/api/public-apis/health');
      if (res.ok) {
        const data = await res.json();
        setPublicApisStatus(data.apis || []);
      }
    } catch (err) {
      console.error('Failed to fetch public apis health:', err);
    } finally {
      setIsLoadingPublicApis(false);
    }
  };

  useEffect(() => {
    if (activeSubTab === 'publicApis') {
      fetchPublicApisHealth();
    }
  }, [activeSubTab]);

  const fetchSatelliteSettings = async () => {
    setIsLoadingSatellite(true);
    try {
      const res = await fetch('/api/admin/satellite-settings');
      if (res.ok) {
        const data = await res.json();
        setUp42ProjectId(data.up42_project_id || '');
        setUp42ProjectSecret(data.up42_project_secret || '');
        setUp42Env(data.up42_env || 'production');
        setPlanetApiKey(data.planet_api_key || '');
        setMaxarApiToken(data.maxar_api_token || '');
      }
    } catch (err) {
      console.error('Error fetching satellite settings:', err);
    } finally {
      setIsLoadingSatellite(false);
    }
  };

  const handleSaveSatelliteSettings = async () => {
    setIsSavingSatellite(true);
    setSatelliteSaveResult({ status: 'idle', message: '' });
    try {
      const res = await fetch('/api/admin/satellite-settings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          up42_project_id: up42ProjectId,
          up42_project_secret: up42ProjectSecret,
          up42_env: up42Env,
          planet_api_key: planetApiKey,
          maxar_api_token: maxarApiToken
        })
      });
      const data = await res.json();
      if (res.ok && data.status === 'success') {
        setSatelliteSaveResult({
          status: 'success',
          message: language === 'uk' ? 'Ключі та реєстраційні дані супутникових API успішно збережено!' : 'Satellite API credentials successfully saved!'
        });
      } else {
        setSatelliteSaveResult({
          status: 'error',
          message: data.error || 'Помилка збереження налаштувань'
        });
      }
    } catch (err: any) {
      setSatelliteSaveResult({
        status: 'error',
        message: err?.message || 'Помилка мережі при збереженні'
      });
    } finally {
      setIsSavingSatellite(false);
    }
  };

  const handleTestUp42Connection = async () => {
    setIsTestingUp42(true);
    setUp42TestResult({ status: 'idle', message: '' });
    try {
      const res = await fetch('/api/admin/satellite-settings/test', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          up42_project_id: up42ProjectId,
          up42_project_secret: up42ProjectSecret,
          up42_env: up42Env
        })
      });
      const data = await res.json();
      if (res.ok && data.status === 'success') {
        setUp42TestResult({
          status: 'success',
          message: data.message
        });
      } else {
        setUp42TestResult({
          status: 'error',
          message: data.error || 'Не вдалося підключитися до UP42 API'
        });
      }
    } catch (err: any) {
      setUp42TestResult({
        status: 'error',
        message: err?.message || 'Помилка мережі при спробі авторизації'
      });
    } finally {
      setIsTestingUp42(false);
    }
  };

  const fetchNcuvkzSettings = async () => {
    setIsLoadingNcuvkz(true);
    try {
      const res = await fetch('/api/admin/ncuvkz-settings');
      if (res.ok) {
        const data = await res.json();
        setCopernicusMirrorUrl(data.copernicus_mirror_url || 'https://copernicus.spacecenter.gov.ua/api/stac');
        setCopernicusApiKey(data.copernicus_api_key || '');
        setCopernicusS3Bucket(data.copernicus_s3_bucket || 's3://copernicus-mirror-ua/sentinel/');
        setNtripCasterHost(data.ntrip_caster_host || 'ntrip.spacecenter.gov.ua');
        setNtripCasterPort(data.ntrip_caster_port || '2101');
        setNtripUser(data.ntrip_user || 'nis_eco_client');
        setNtripPassword(data.ntrip_password || '');
        setNtripMountpoints(data.ntrip_mountpoints || 'DNIP00UKR0,ZAPO00UKR0,KHER00UKR0,UZHL00UKR0,KYIV00UKR0');
        setSknzuRinexFtp(data.sknzu_rinex_ftp || 'ftp://rinex.spacecenter.gov.ua/pub/rinex/');
        setSeedlinkHost(data.seedlink_host || 'seedlink.gcsk.gov.ua');
        setSeedlinkPort(data.seedlink_port || '18000');
        setSeedlinkChannels(data.seedlink_channels || 'PS45.MALYN.HHZ,BALT.HHZ,UZHG.HHZ');
        setGcskEventsApi(data.gcsk_events_api || 'https://events.gcsk.gov.ua/api/v1/bulletins');
        setNcuvkzDaemonEnabled(data.ncuvkz_daemon_enabled !== false);
        setNcuvkzDaemonInterval(String(data.ncuvkz_daemon_interval_minutes || '15'));
        setNcuvkzOperatorChatId(data.ncuvkz_operator_telegram_chat_id || '1421638496');
      }
    } catch (err) {
      console.error('Failed to fetch NCUVKZ settings:', err);
    } finally {
      setIsLoadingNcuvkz(false);
    }
  };

  const handleSaveNcuvkzSettings = async () => {
    setIsSavingNcuvkz(true);
    setNcuvkzSaveResult({ status: 'idle', message: '' });
    try {
      const res = await fetch('/api/admin/ncuvkz-settings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          copernicus_mirror_url: copernicusMirrorUrl,
          copernicus_api_key: copernicusApiKey,
          copernicus_s3_bucket: copernicusS3Bucket,
          ntrip_caster_host: ntripCasterHost,
          ntrip_caster_port: ntripCasterPort,
          ntrip_user: ntripUser,
          ntrip_password: ntripPassword,
          ntrip_mountpoints: ntripMountpoints,
          sknzu_rinex_ftp: sknzuRinexFtp,
          seedlink_host: seedlinkHost,
          seedlink_port: seedlinkPort,
          seedlink_channels: seedlinkChannels,
          gcsk_events_api: gcskEventsApi,
          ncuvkz_daemon_enabled: ncuvkzDaemonEnabled,
          ncuvkz_daemon_interval_minutes: ncuvkzDaemonInterval,
          ncuvkz_operator_telegram_chat_id: ncuvkzOperatorChatId
        })
      });
      const data = await res.json();
      if (res.ok && data.status === 'success') {
        setNcuvkzSaveResult({
          status: 'success',
          message: language === 'uk' ? 'Налаштування шлюзів НЦУВКЗ (Copernicus, СКНЗУ, ГЦСК) та режиму 24/7 успішно збережено!' : 'NCUVKZ gateway and 24/7 settings successfully saved!'
        });
      } else {
        setNcuvkzSaveResult({
          status: 'error',
          message: data.error || 'Помилка збереження налаштувань'
        });
      }
    } catch (err: any) {
      setNcuvkzSaveResult({
        status: 'error',
        message: err?.message || 'Помилка мережі при збереженні'
      });
    } finally {
      setIsSavingNcuvkz(false);
    }
  };

  const handleTestNcuvkzConnection = async () => {
    setIsTestingNcuvkz(true);
    setNcuvkzTestResult({ status: 'idle', message: '' });
    try {
      const res = await fetch('/api/admin/ncuvkz-settings/test', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          copernicus_mirror_url: copernicusMirrorUrl,
          copernicus_api_key: copernicusApiKey,
          ntrip_caster_host: ntripCasterHost,
          ntrip_caster_port: ntripCasterPort,
          ntrip_password: ntripPassword,
          seedlink_host: seedlinkHost,
          seedlink_port: seedlinkPort
        })
      });
      const data = await res.json();
      if (res.ok && (data.status === 'success' || data.status === 'warning' || data.status === 'error')) {
        setNcuvkzTestResult({
          status: data.status,
          message: data.message,
          serverIp: data.serverIp,
          gateways: data.gateways,
          operatorChecklist: data.operatorChecklist,
          details: data.details
        });
      } else {
        setNcuvkzTestResult({
          status: 'error',
          message: data.error || 'Не вдалося перевірити з\'єднання зі шлюзами'
        });
      }
    } catch (err: any) {
      setNcuvkzTestResult({
        status: 'error',
        message: err?.message || 'Помилка мережі при перевірці'
      });
    } finally {
      setIsTestingNcuvkz(false);
    }
  };

  const handlePingOperatorTelegram = async () => {
    setIsPingingTelegram(true);
    setNcuvkzPingResult({ status: 'idle', message: '' });
    try {
      const res = await fetch('/api/admin/ncuvkz-settings/ping-telegram', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          chat_id: ncuvkzOperatorChatId
        })
      });
      const data = await res.json();
      if (res.ok && data.status === 'success') {
        setNcuvkzPingResult({
          status: 'success',
          message: data.message
        });
      } else {
        setNcuvkzPingResult({
          status: 'error',
          message: data.error || 'Помилка відправки в Telegram'
        });
      }
    } catch (err: any) {
      setNcuvkzPingResult({
        status: 'error',
        message: err?.message || 'Помилка мережі при відправці пінг-сигналу'
      });
    } finally {
      setIsPingingTelegram(false);
    }
  };

  const fetchTelegramSettings = async () => {
    setIsLoadingTelegram(true);
    try {
      const res = await fetch('/api/admin/telegram-settings');
      if (res.ok) {
        const data = await res.json();
        setTelegramBotToken(data.telegram_bot_token || '');
        setTelegramChatId(data.telegram_chat_id || '');
        setTelegramPublicChannelId(data.telegram_public_channel_id || '');
        setMarketRadarPublicChannelAutopost(data.market_radar_public_channel_autopost !== false);
        setLastMarketRadarPostedAt(data.last_market_radar_public_channel_posted_at || null);
      }
    } catch (err) {
      console.error("Failed to fetch telegram settings", err);
    } finally {
      setIsLoadingTelegram(false);
    }
  };

  const fetchPublicDigest = async () => {
    setIsLoadingPublicDigest(true);
    try {
      const res = await fetch('/api/admin/market-radar/public-digest');
      if (res.ok) {
        const data = await res.json();
        if (data.digest) {
          setCachedPublicDigest(data.digest);
        }
        if (data.publicChannelId && !telegramPublicChannelId) {
          setTelegramPublicChannelId(data.publicChannelId);
        }
        if (data.lastPostedAt) {
          setLastMarketRadarPostedAt(data.lastPostedAt);
        }
      }
    } catch (err) {
      console.error("Failed to fetch public digest", err);
    } finally {
      setIsLoadingPublicDigest(false);
    }
  };

  const handleSaveTelegramSettings = async () => {
    setIsSavingTelegram(true);
    try {
      const res = await fetch('/api/admin/telegram-settings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          telegram_bot_token: telegramBotToken,
          telegram_chat_id: telegramChatId,
          telegram_public_channel_id: telegramPublicChannelId,
          market_radar_public_channel_autopost: marketRadarPublicChannelAutopost
        })
      });
      if (res.ok) {
        alert(language === 'uk' ? 'Налаштування Telegram успішно збережено!' : 'Telegram settings successfully saved!');
      } else {
        const err = await res.json();
        alert(err.error || 'Помилка збереження налаштувань');
      }
    } catch (err) {
      console.error(err);
      alert('Помилка з\'єднання з сервером');
    } finally {
      setIsSavingTelegram(false);
    }
  };

  const handleTestPublicChannel = async () => {
    if (!telegramPublicChannelId.trim()) {
      setPublicChannelStatusMsg({
        type: 'error',
        text: language === 'uk' ? 'Вкажіть ID або юзернейм каналу (@ChannelName або -100...)' : 'Please specify Channel ID or username'
      });
      return;
    }
    setIsTestingPublicChannel(true);
    setPublicChannelStatusMsg(null);
    try {
      const res = await fetch('/api/admin/market-radar/test-public-channel', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ channel_id: telegramPublicChannelId.trim() })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setPublicChannelStatusMsg({ type: 'success', text: data.message });
      } else {
        setPublicChannelStatusMsg({ type: 'error', text: data.error || 'Помилка тестування каналу' });
      }
    } catch (err: any) {
      setPublicChannelStatusMsg({ type: 'error', text: err.message || 'Помилка підключення до сервера' });
    } finally {
      setIsTestingPublicChannel(false);
    }
  };

  const handlePublishPublicChannel = async () => {
    if (!telegramPublicChannelId.trim()) {
      setPublicChannelStatusMsg({
        type: 'error',
        text: language === 'uk' ? 'Вкажіть ID або юзернейм каналу (@ChannelName або -100...)' : 'Please specify Channel ID or username'
      });
      return;
    }
    setIsPublishingPublicChannel(true);
    setPublicChannelStatusMsg(null);
    try {
      const res = await fetch('/api/admin/market-radar/publish-public-channel', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ channel_id: telegramPublicChannelId.trim() })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setPublicChannelStatusMsg({ type: 'success', text: data.message });
        setLastMarketRadarPostedAt(new Date().toISOString());
      } else {
        setPublicChannelStatusMsg({ type: 'error', text: data.error || 'Помилка публікації в канал' });
      }
    } catch (err: any) {
      setPublicChannelStatusMsg({ type: 'error', text: err.message || 'Помилка підключення до сервера' });
    } finally {
      setIsPublishingPublicChannel(false);
    }
  };

  const fetchEmailSettingsAndLogs = async () => {
    setIsLoadingEmail(true);
    try {
      const [smtpRes, logsRes] = await Promise.all([
        fetch('/api/admin/smtp-settings'),
        fetch('/api/admin/email-logs')
      ]);
      if (smtpRes.ok) {
        const data = await smtpRes.json();
        if (data.smtp) setSmtpSettings(data.smtp);
      }
      if (logsRes.ok) {
        const data = await logsRes.json();
        if (data.logs) setEmailLogs(data.logs);
      }
      await fetchEmailQueueStats();
    } catch (err) {
      console.error("Failed to fetch email settings or logs", err);
    } finally {
      setIsLoadingEmail(false);
    }
  };

  const handleSaveSmtpSettings = async () => {
    setIsSavingSmtp(true);
    try {
      const res = await fetch('/api/admin/smtp-settings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(smtpSettings)
      });
      if (res.ok) {
        alert(language === 'uk' ? 'Налаштування SMTP успішно збережено!' : 'SMTP settings successfully saved!');
      } else {
        const err = await res.json();
        alert(err.error || 'Помилка збереження SMTP');
      }
    } catch (err) {
      console.error(err);
      alert('Помилка з\'єднання з сервером');
    } finally {
      setIsSavingSmtp(false);
    }
  };

  const [isTestingSmtp, setIsTestingSmtp] = useState(false);
  const [smtpTestResult, setSmtpTestResult] = useState<{ success: boolean; message: string } | null>(null);

  // Smart Email Queue state
  const [emailQueueStats, setEmailQueueStats] = useState<{
    totalInQueue: number;
    pendingCount: number;
    pendingUkraine?: number;
    pendingPoland?: number;
    pendingEu?: number;
    sentToday: number;
    sentTotal: number;
    failedCount: number;
    dailyLimit: number;
    isQuotaLocked: boolean;
    quotaResetTime?: string | null;
  } | null>(null);
  const [isProcessingQueue, setIsProcessingQueue] = useState(false);
  const [isEnqueuingQueue, setIsEnqueuingQueue] = useState(false);
  const [queueActionMsg, setQueueActionMsg] = useState<string | null>(null);

  const fetchEmailQueueStats = async () => {
    try {
      const res = await fetch('/api/admin/email-queue/status');
      if (res.ok) {
        const data = await res.json();
        if (data.stats) setEmailQueueStats(data.stats);
      }
    } catch (err) {
      console.error("Failed to fetch email queue stats:", err);
    }
  };

  const handleEnqueueAllLeads = async () => {
    if (!confirm(language === 'uk' ? 'Додати всіх користувачів та лідів (1900+) до черги автоматичної щоденної відправки?' : 'Enqueue all leads and users to the smart daily rate-limited queue?')) {
      return;
    }
    setIsEnqueuingQueue(true);
    setQueueActionMsg(null);
    try {
      const res = await fetch('/api/admin/email-queue/enqueue-all', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customNote: invitationCustomNote.trim() || undefined,
          language: language,
          autoStart: true
        })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setQueueActionMsg(data.message || 'Контакти успішно додано до черги!');
        if (data.stats) setEmailQueueStats(data.stats);
        fetchEmailSettingsAndLogs();
      } else {
        alert(data.error || 'Помилка додавання до черги');
      }
    } catch (err) {
      console.error(err);
      alert('Помилка з\'єднання з сервером');
    } finally {
      setIsEnqueuingQueue(false);
    }
  };

  const handleProcessQueueNow = async (force: boolean = false) => {
    setIsProcessingQueue(true);
    setQueueActionMsg(null);
    try {
      const res = await fetch('/api/admin/email-queue/process-now', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ forceNow: force })
      });
      const data = await res.json();
      if (data.message) {
        setQueueActionMsg(data.message);
      }
      if (data.stats) setEmailQueueStats(data.stats);
      fetchEmailSettingsAndLogs();
    } catch (err: any) {
      setQueueActionMsg(`Помилка: ${err.message || 'Збій запуску черги'}`);
    } finally {
      setIsProcessingQueue(false);
    }
  };

  const handleClearFailedQueue = async () => {
    try {
      const res = await fetch('/api/admin/email-queue/clear-failed', { method: 'POST' });
      const data = await res.json();
      if (data.stats) setEmailQueueStats(data.stats);
      alert(data.message || 'Помилки скинуто');
    } catch (err) {
      console.error(err);
    }
  };

  const handleTestSmtpConnection = async () => {
    setIsTestingSmtp(true);
    setSmtpTestResult(null);
    try {
      const res = await fetch('/api/admin/test-smtp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(smtpSettings)
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setSmtpTestResult({ success: true, message: data.message });
      } else {
        setSmtpTestResult({ success: false, message: data.error || 'Помилка тестування SMTP' });
      }
    } catch (err: any) {
      setSmtpTestResult({ success: false, message: err.message || 'Помилка з\'єднання' });
    } finally {
      setIsTestingSmtp(false);
    }
  };

  const handleTriggerInactivityReminders = async (force: boolean) => {
    setIsSendingInactivityReminder(true);
    setInactivityReminderResult(null);
    try {
      const res = await fetch('/api/admin/trigger-inactivity-reminders', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ force })
      });
      if (res.ok) {
        const data = await res.json();
        setInactivityReminderResult(data.result?.message || 'Розсилку нагадувань успішно запущено!');
        fetchEmailSettingsAndLogs();
      } else {
        const err = await res.json();
        alert(err.error || 'Помилка запуску розсилки');
      }
    } catch (err) {
      console.error(err);
      alert('Помилка з\'єднання з сервером');
    } finally {
      setIsSendingInactivityReminder(false);
    }
  };

  const handleSendInvitationAll = async () => {
    if (!confirm(language === 'uk' ? 'Ви дійсно бажаєте надіслати запрошення усім користувачам платформи?' : 'Are you sure you want to send invitation emails to all users?')) {
      return;
    }
    setIsSendingInvitationAll(true);
    setInvitationAllResult(null);
    try {
      const res = await fetch('/api/admin/send-invitation-all', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customNote: invitationCustomNote.trim() || undefined })
      });
      if (res.ok) {
        const data = await res.json();
        setInvitationAllResult(data.result?.message || (language === 'uk' ? 'Розсилку запрошень успішно виконано!' : 'Invitation broadcast completed successfully!'));
        fetchEmailSettingsAndLogs();
      } else {
        const err = await res.json();
        alert(err.error || 'Помилка розсилки запрошень');
      }
    } catch (err) {
      console.error(err);
      alert('Помилка з\'єднання з сервером');
    } finally {
      setIsSendingInvitationAll(false);
    }
  };

  const fetchPricingSettings = async () => {
    setIsLoadingPricing(true);
    try {
      const res1 = await fetch('/api/admin/agent-settings');
      if (res1.ok) {
        const data = await res1.json();
        setAgentSettings(data);
      }
      
      const res2 = await fetch('/api/admin/system-settings');
      if (res2.ok) {
        const data = await res2.json();
        setMonobankToken(data.monobank_token || '');
      }
    } catch (err) {
      console.error("Failed to fetch pricing settings", err);
    } finally {
      setIsLoadingPricing(false);
    }
  };

  const handleSaveMonobankToken = async () => {
    setIsSavingToken(true);
    try {
      const res = await fetch('/api/admin/system-settings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ monobank_token: monobankToken })
      });
      if (res.ok) {
        alert(language === 'uk' ? 'Токен Monobank успішно збережено!' : 'Monobank token successfully saved!');
      } else {
        const err = await res.json();
        alert(err.error || 'Помилка збереження токена');
      }
    } catch (err) {
      console.error(err);
      alert('Помилка з\'єднання з сервером');
    } finally {
      setIsSavingToken(false);
    }
  };

  const handleTestMonobank = async () => {
    setIsTestingMono(true);
    setMonoTestResult(null);
    try {
      const res = await fetch('/api/admin/test-monobank', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: monobankToken })
      });
      const data = await res.json();
      setMonoTestResult(data);
    } catch (err: any) {
      setMonoTestResult({
        ok: false,
        message: err.message || 'Помилка зв\'язку з сервером при тестуванні токена'
      });
    } finally {
      setIsTestingMono(false);
    }
  };

  const updateAutoSinglePrice = (agentId: string) => {
    const limitInput = document.getElementById(`limit_${agentId}`) as HTMLInputElement;
    const priceInput = document.getElementById(`price_${agentId}`) as HTMLInputElement;
    const singlePriceInput = document.getElementById(`single_price_${agentId}`) as HTMLInputElement;
    if (limitInput && priceInput && singlePriceInput) {
      const limit = parseInt(limitInput.value) || 0;
      const price = parseFloat(priceInput.value) || 0;
      if (limit > 0 && price > 0) {
        const calcSingle = Math.round((price / limit) * 1.5);
        singlePriceInput.value = calcSingle.toString();
      }
    }
  };

  const handleSaveAgentSettings = async (agentId: string, limit: number, price: number, durationDays: number, singlePrice: number) => {
    setSavingAgentId(agentId);
    try {
      const res = await fetch('/api/admin/agent-settings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ agentId, limit, price, durationDays, singlePrice })
      });
      if (res.ok) {
        alert(language === 'uk' ? `Тарифи для ${getAgentName(agentId)} збережено!` : `Pricing for ${getAgentName(agentId)} saved!`);
        await fetchPricingSettings();
      } else {
        const err = await res.json();
        alert(err.error || 'Помилка збереження тарифів');
      }
    } catch (err) {
      console.error(err);
      alert('Помилка з\'єднання з сервером');
    } finally {
      setSavingAgentId(null);
    }
  };

  const getAgentName = (agentId: string) => {
    const agent = agentsConfig.find(a => a.id === agentId);
    if (agent) {
      return language === 'uk' ? agent.nameUk : agent.nameEn;
    }
    switch (agentId) {
      case 'deep-research': return language === 'uk' ? 'Deep Research' : 'Deep Research';
      case 'corrector': return language === 'uk' ? 'Коректор документів' : 'Document Corrector';
      case 'koshtorisnyk': return language === 'uk' ? 'Кошторисник' : 'Estimator';
      case 'video-renderer': return language === 'uk' ? 'Медіа-Рендерер' : 'Media Renderer';
      case 'contract-auditor': return language === 'uk' ? 'Юридичний Аудитор' : 'Contract Auditor';
      case 'enterprise-dashboard': return language === 'uk' ? 'Кабінет підприємства & To-Do' : 'Enterprise Cabinet & To-Do';
      default: return agentId;
    }
  };

  const fetchUsers = async () => {
    setIsLoadingUsers(true);
    try {
      const response = await fetch('/api/admin/users');
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      } else {
        console.error("Failed to fetch users");
      }
    } catch (err) {
      console.error("Failed to fetch users", err);
    } finally {
      setIsLoadingUsers(false);
    }
  };

  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newUserEmail.trim() || !newUserEmail.includes('@')) {
      setAddUserError(language === 'uk' ? 'Введіть коректну електронну пошту (email)' : 'Please enter a valid email address');
      return;
    }
    setIsAddingUser(true);
    setAddUserSuccess(null);
    setAddUserError(null);
    try {
      const response = await fetch('/api/admin/users/add', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: newUserEmail.trim(),
          fullName: newUserName.trim() || undefined,
          unlimitedAll: newUserUnlimited
        })
      });
      const data = await response.json();
      if (response.ok) {
        setAddUserSuccess(data.message || (language === 'uk' ? 'Користувача успішно додано та схвалено!' : 'User successfully added and approved!'));
        setNewUserEmail('');
        setNewUserName('');
        setNewUserUnlimited(false);
        fetchUsers();
      } else {
        setAddUserError(data.error || (language === 'uk' ? 'Не вдалося додати користувача' : 'Failed to add user'));
      }
    } catch (err) {
      console.error(err);
      setAddUserError(language === 'uk' ? 'Помилка мережі при додаванні користувача' : 'Network error while adding user');
    } finally {
      setIsAddingUser(false);
    }
  };

  const handleUpdateStatus = async (email: string, newStatus: 'approved' | 'rejected') => {
    setUpdatingUserEmail(email);
    try {
      const response = await fetch(`/api/admin/users/${encodeURIComponent(email)}/status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });
      if (response.ok) {
        setUsers(prev => prev.map(u => u.email === email ? { ...u, status: newStatus } : u));
      } else {
        const err = await response.json().catch(() => ({}));
        alert(`Помилка: ${err.error || 'Не вдалося оновити статус'}`);
      }
    } catch (err) {
      console.error(err);
      alert('Помилка мережі при спробі оновити статус');
    } finally {
      setUpdatingUserEmail(null);
    }
  };

  const handleToggle2FA = async (email: string, currentEnabled: boolean) => {
    setUpdatingUserEmail(email);
    try {
      const response = await fetch(`/api/admin/users/${encodeURIComponent(email)}/2fa`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ twoFactorEnabled: !currentEnabled })
      });
      if (response.ok) {
        setUsers(prev => prev.map(u => u.email === email ? { ...u, twoFactorEnabled: !currentEnabled } : u));
      } else {
        const err = await response.json().catch(() => ({}));
        alert(`Помилка: ${err.error || 'Не вдалося змінити налаштування другого фактора'}`);
      }
    } catch (err) {
      console.error(err);
      alert('Помилка мережі при зміні вимоги другого фактора');
    } finally {
      setUpdatingUserEmail(null);
    }
  };

  const handleReset2FA = async (email: string) => {
    const confirmation = language === 'uk'
      ? `Ви дійсно хочете скинути пароль другого фактора для ${email}? Користувачу доведеться встановити новий пароль при наступному вході.`
      : `Are you sure you want to reset the second factor password for ${email}? The user will have to set a new password upon their next login.`;

    if (!window.confirm(confirmation)) {
      return;
    }

    setUpdatingUserEmail(email);
    try {
      const response = await fetch(`/api/admin/users/${encodeURIComponent(email)}/2fa`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ resetPassword: true })
      });
      if (response.ok) {
        setUsers(prev => prev.map(u => u.email === email ? { ...u, twoFactorEnabled: false, has2FASecret: false, hasPassword: false } : u));
        const msg = language === 'uk' ? 'Пароль другого фактора успішно скинуто.' : 'Second factor password successfully reset.';
        alert(msg);
      } else {
        const err = await response.json().catch(() => ({}));
        alert(`Помилка: ${err.error || 'Не вдалося скинути пароль'}`);
      }
    } catch (err) {
      console.error(err);
      alert('Помилка мережі при скиданні пароля');
    } finally {
      setUpdatingUserEmail(null);
    }
  };

  const handleOpenUnlimitedModal = (user: UserProfileData) => {
    setUnlimitedModalUser(user);
    if (user.unlimitedAll) {
      setUnlimitedMode('all');
      setSelectedAgents([]);
    } else if (user.unlimitedAgents && user.unlimitedAgents.length > 0) {
      setUnlimitedMode('selected');
      setSelectedAgents([...user.unlimitedAgents]);
    } else {
      setUnlimitedMode('none');
      setSelectedAgents([]);
    }
  };

  const handleSaveUnlimited = async () => {
    if (!unlimitedModalUser) return;
    setIsSavingUnlimited(true);
    try {
      const payload = {
        unlimitedAll: unlimitedMode === 'all',
        unlimitedAgents: unlimitedMode === 'selected' ? selectedAgents : []
      };
      const res = await fetch(`/api/admin/users/${encodeURIComponent(unlimitedModalUser.email)}/unlimited`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (res.ok) {
        setUsers(prev => prev.map(u => u.email === unlimitedModalUser.email ? {
          ...u,
          unlimitedAll: payload.unlimitedAll,
          unlimitedAgents: payload.unlimitedAgents
        } : u));
        setUnlimitedModalUser(null);
        alert(language === 'uk' ? 'Налаштування безлімітного доступу успішно збережено!' : 'Unlimited access settings successfully saved!');
      } else {
        const err = await res.json().catch(() => ({}));
        alert(`Помилка: ${err.error || 'Не вдалося зберегти налаштування'}`);
      }
    } catch (err) {
      console.error(err);
      alert('Помилка мережі при збереженні налаштувань безлімітного доступу');
    } finally {
      setIsSavingUnlimited(false);
    }
  };

  const handleSave = async () => {
    const trimmed = apiKey.trim();
    if (!trimmed) {
      localStorage.removeItem('custom_gemini_api_key');
      setSavedKey('');
      setApiKey('');
      setTestResult({
        status: 'idle',
        message: ''
      });
      return;
    }

    setIsTesting(true);
    try {
      const response = await fetch('/api/save-key', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ apiKey: trimmed })
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || 'Failed to save key on server');
      }

      localStorage.setItem('custom_gemini_api_key', trimmed);
      setSavedKey(trimmed);
      setTestResult({
        status: 'idle',
        message: ''
      });

      const msg = language === 'uk' 
        ? 'Ключ API успішно збережено в системі!' 
        : 'API Key successfully saved in the system!';
      alert(msg);
    } catch (err: any) {
      alert(language === 'uk' 
        ? `Помилка збереження на сервері: ${err.message}` 
        : `Failed to save on server: ${err.message}`);
    } finally {
      setIsTesting(false);
    }
  };

  const handleClear = () => {
    const confirmClear = window.confirm(
      language === 'uk' 
        ? 'Ви дійсно хочете видалити збережений ключ API з локального сховища?' 
        : 'Are you sure you want to delete the saved API key from local storage?'
    );
    if (confirmClear) {
      localStorage.removeItem('custom_gemini_api_key');
      setSavedKey('');
      setApiKey('');
      setTestResult({
        status: 'idle',
        message: ''
      });
    }
  };

  const handleTestConnection = async () => {
    const keyToTest = apiKey.trim();
    if (!keyToTest) {
      setTestResult({
        status: 'error',
        message: language === 'uk' 
          ? 'Будь ласка, введіть ключ перед тестуванням.' 
          : 'Please enter a key before testing.'
      });
      return;
    }

    setIsTesting(true);
    setTestResult({ status: 'idle', message: '' });

    try {
      const response = await fetch('/api/test-key', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': keyToTest
        },
        body: JSON.stringify({ apiKey: keyToTest })
      });

      const data = await response.json();

      if (response.ok && data.status === 'success') {
        setTestResult({
          status: 'success',
          message: language === 'uk' 
            ? 'Підключення успішне! Ключ API повністю працездатний.' 
            : 'Connection successful! Your API Key is fully functional.'
        });
      } else {
        setTestResult({
          status: 'error',
          message: data.error || (language === 'uk' ? 'Помилка валідації ключа.' : 'Key validation failed.')
        });
      }
    } catch (err: any) {
      setTestResult({
        status: 'error',
        message: err.message || (language === 'uk' ? 'Помилка мережі при спробі підключення.' : 'Network error occurred during connection.')
      });
    } finally {
      setIsTesting(false);
    }
  };

  const filteredUsers = users.filter(user => 
    user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.displayName.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-6 sm:p-8 space-y-6 sm:space-y-8 shadow-xs max-w-4xl mx-auto">
      
      {/* Subtab Navigation */}
      <div className="flex border-b border-slate-200 font-mono text-[11px] font-bold pb-3 gap-2 flex-wrap">
        <button
          onClick={() => setActiveSubTab('apiKey')}
          className={`py-1.5 px-4 rounded transition-all cursor-pointer flex items-center gap-1.5 ${
            activeSubTab === 'apiKey'
              ? 'bg-[#1E3A8A] text-white'
              : 'bg-slate-100 text-slate-500 hover:bg-slate-200 hover:text-slate-800'
          }`}
        >
          <Key className="w-3.5 h-3.5" />
          <span>{language === 'pl' ? 'KLUCZ GEMINI API' : language === 'uk' ? 'КЛЮЧ GEMINI API' : 'GEMINI API KEY'}</span>
        </button>
        <button
          onClick={() => setActiveSubTab('users')}
          className={`py-1.5 px-4 rounded transition-all cursor-pointer flex items-center gap-1.5 ${
            activeSubTab === 'users'
              ? 'bg-[#1E3A8A] text-white'
              : 'bg-slate-100 text-slate-500 hover:bg-slate-200 hover:text-slate-800'
          }`}
        >
          <Users className="w-3.5 h-3.5" />
          <span>{language === 'pl' ? 'DOSTĘPY & 2FA' : language === 'uk' ? 'КЕРУВАННЯ ДОСТУПОМ & 2FA' : 'USER ACCESS & 2FA'}</span>
        </button>
        <button
          onClick={() => setActiveSubTab('pricing')}
          className={`py-1.5 px-4 rounded transition-all cursor-pointer flex items-center gap-1.5 ${
            activeSubTab === 'pricing'
              ? 'bg-[#1E3A8A] text-white'
              : 'bg-slate-100 text-slate-500 hover:bg-slate-200 hover:text-slate-800'
          }`}
        >
          <Users className="w-3.5 h-3.5" />
          <span>{language === 'pl' ? 'CENNIK & PŁATNOŚCI' : language === 'uk' ? 'ТАРИФИ & ПЛАТЕЖІ' : 'PRICING & PAYMENTS'}</span>
        </button>
        <button
          onClick={() => setActiveSubTab('satellite')}
          className={`py-1.5 px-4 rounded transition-all cursor-pointer flex items-center gap-1.5 ${
            activeSubTab === 'satellite'
              ? 'bg-[#1E3A8A] text-white'
              : 'bg-slate-100 text-slate-500 hover:bg-slate-200 hover:text-slate-800'
          }`}
        >
          <Globe className="w-3.5 h-3.5 text-cyan-400" />
          <span>{language === 'pl' ? '🛰️ KLUCZE SATELITARNE (UP42)' : language === 'uk' ? '🛰️ СУПУТНИКОВІ КЛЮЧІ (UP42)' : '🛰️ SATELLITE KEYS (UP42)'}</span>
        </button>
        <button
          onClick={() => setActiveSubTab('ncuvkz')}
          className={`py-1.5 px-4 rounded transition-all cursor-pointer flex items-center gap-1.5 ${
            activeSubTab === 'ncuvkz'
              ? 'bg-emerald-700 text-white font-bold shadow'
              : 'bg-emerald-50 text-emerald-800 border border-emerald-200 hover:bg-emerald-100'
          }`}
        >
          <Activity className="w-3.5 h-3.5 text-emerald-300 animate-pulse" />
          <span>{language === 'pl' ? '🛰️ BRAMY NCUOKS & NIS-EKO' : language === 'uk' ? '🛰️ ШЛЮЗИ НЦУВКЗ & НІС-ЕКО' : '🛰️ NCUVKZ & NIS-ECO GATEWAY'}</span>
        </button>
        <button
          onClick={() => setActiveSubTab('telegram')}
          className={`py-1.5 px-4 rounded transition-all cursor-pointer flex items-center gap-1.5 ${
            activeSubTab === 'telegram'
              ? 'bg-[#1E3A8A] text-white'
              : 'bg-slate-100 text-slate-500 hover:bg-slate-200 hover:text-slate-800'
          }`}
        >
          <Send className="w-3.5 h-3.5" />
          <span>{language === 'pl' ? 'BOT TELEGRAM' : language === 'uk' ? 'TELEGRAM БОТ' : 'TELEGRAM BOT'}</span>
        </button>
        <button
          onClick={() => setActiveSubTab('email')}
          className={`py-1.5 px-4 rounded transition-all cursor-pointer flex items-center gap-1.5 ${
            activeSubTab === 'email'
              ? 'bg-[#1E3A8A] text-white'
              : 'bg-slate-100 text-slate-500 hover:bg-slate-200 hover:text-slate-800'
          }`}
        >
          <Mail className="w-3.5 h-3.5" />
          <span>{language === 'pl' ? 'WYSYŁKA E-MAIL' : language === 'uk' ? 'EMAIL РОЗСИЛКА' : 'EMAIL MAILING'}</span>
        </button>
        <button
          onClick={() => setActiveSubTab('emailMonitors')}
          className={`py-1.5 px-4 rounded transition-all cursor-pointer flex items-center gap-1.5 ${
            activeSubTab === 'emailMonitors'
              ? 'bg-[#1E3A8A] text-white'
              : 'bg-slate-100 text-slate-500 hover:bg-slate-200 hover:text-slate-800'
          }`}
        >
          <Mail className="w-3.5 h-3.5 text-emerald-500" />
          <span>{language === 'pl' ? '📮 MONITORING POCZTY' : language === 'uk' ? '📮 МОНІТОРИНГ UKR.NET / GMAIL' : '📮 EMAIL AUTO-MONITORING'}</span>
        </button>
        <button
          onClick={() => setActiveSubTab('agentStats')}
          className={`py-1.5 px-4 rounded transition-all cursor-pointer flex items-center gap-1.5 ${
            activeSubTab === 'agentStats'
              ? 'bg-[#1E3A8A] text-white'
              : 'bg-slate-100 text-slate-500 hover:bg-slate-200 hover:text-slate-800'
          }`}
        >
          <BarChart3 className="w-3.5 h-3.5" />
          <span>{language === 'pl' ? 'STATYSTYKI AGENTÓW' : language === 'uk' ? 'СТАТИСТИКА АГЕНТІВ' : 'AGENT STATISTICS'}</span>
        </button>
        <button
          onClick={() => setActiveSubTab('publicApis')}
          className={`py-1.5 px-4 rounded transition-all cursor-pointer flex items-center gap-1.5 ${
            activeSubTab === 'publicApis'
              ? 'bg-[#1E3A8A] text-white'
              : 'bg-slate-100 text-slate-500 hover:bg-slate-200 hover:text-slate-800'
          }`}
        >
          <Globe className="w-3.5 h-3.5" />
          <span>{language === 'pl' ? '🛰️ OTWARTE API' : language === 'uk' ? '🛰️ ВІДКРИТІ API' : '🛰️ PUBLIC APIS'}</span>
        </button>
      </div>

      {activeSubTab === 'apiKey' ? (
        <div className="space-y-6">
          {/* Title Header */}
          <div className="flex items-start gap-4 pb-6 border-b border-slate-200">
            <div className="p-3 bg-red-50 rounded border border-red-200 text-red-500">
              <Key className="w-6 h-6 animate-pulse" />
            </div>
            <div className="space-y-1">
              <h2 className="text-base font-bold font-mono uppercase tracking-wider text-slate-800">
                {language === 'uk' ? 'НАЛАШТУВАННЯ КЛЮЧА API' : 'API KEY SETTINGS'}
              </h2>
              <p className="text-[11px] text-slate-400 font-mono">
                {language === 'uk' 
                  ? 'КОНФІГУРАЦІЯ СВОГО КЛЮЧА GOOGLE GEMINI API' 
                  : 'CONFIGURE YOUR PERSONAL GOOGLE GEMINI API ACCESS'}
              </p>
            </div>
          </div>

          {/* Info Warning */}
          <div className="p-4 bg-red-50/50 border border-red-200 rounded flex items-start gap-3.5">
            <ShieldCheck className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
            <div className="space-y-1 text-[11px] font-mono leading-relaxed text-slate-600">
              <p className="font-bold uppercase tracking-wider text-red-500">
                {language === 'uk' ? 'БЕЗПЕКА ТА ЛОКАЛЬНЕ ЗБЕРЕЖЕННЯ' : 'SECURITY & LOCAL PERSISTENCE'}
              </p>
              <p>
                {language === 'uk' 
                  ? 'Введений ключ зберігається виключно у локальному сховищі (localStorage) вашого веб-браузера. Він ніколи не завантажується і не зберігається на нашому сервері як статичний пароль. Він використовується безпосередньо для автентифікації ваших запитів до нейромережі.'
                  : 'The key you enter is stored exclusively in your browser\'s local storage (localStorage). It is never permanently stored on our backend databases or logs. It is used directly as a secure header token to process your neural queries.'}
              </p>
            </div>
          </div>

          {/* Main Form Fields */}
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400 font-mono flex items-center justify-between">
                <span>{language === 'uk' ? 'Google Gemini Ключ API' : 'Google Gemini API Key'}</span>
                {savedKey ? (
                  <span className="text-green-600 font-mono lowercase flex items-center gap-1">
                    <span className="w-1.5 h-1.5 bg-green-500 rounded-full inline-block" />
                    {language === 'uk' ? 'активний ключ збережено' : 'active key saved'}
                  </span>
                ) : (
                  <span className="text-amber-600 font-mono lowercase flex items-center gap-1">
                    <span className="w-1.5 h-1.5 bg-amber-500 rounded-full inline-block animate-ping" />
                    {language === 'uk' ? 'використовується системний' : 'using system default key'}
                  </span>
                )}
              </label>
              
              <div className="relative">
                <input
                  type={showKey ? 'text' : 'password'}
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  placeholder="AIzaSy..."
                  className="w-full bg-slate-50 border border-slate-200 rounded px-4 py-3 text-xs text-slate-800 font-mono placeholder:text-slate-400 focus:outline-none focus:border-red-500 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowKey(!showKey)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors cursor-pointer"
                >
                  {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap items-center gap-3 pt-2">
              <button
                onClick={handleSave}
                disabled={isTesting}
                className="px-4 py-2.5 bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 disabled:bg-slate-100 disabled:text-slate-400 text-white font-bold font-mono text-[11px] uppercase rounded transition-all flex items-center gap-1.5 cursor-pointer shadow-xs"
              >
                <Sparkles className="w-3.5 h-3.5" />
                {language === 'uk' ? 'Зберегти ключ' : 'Save Key'}
              </button>

              {savedKey && (
                <button
                  onClick={handleClear}
                  disabled={isTesting}
                  className="px-4 py-2.5 bg-red-50 hover:bg-red-100 border border-red-200 text-red-600 font-bold font-mono text-[11px] uppercase rounded transition-all flex items-center gap-1.5 cursor-pointer"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  {language === 'uk' ? 'Видалити ключ' : 'Delete Key'}
                </button>
              )}

              <button
                onClick={handleTestConnection}
                disabled={isTesting || !apiKey}
                className="px-4 py-2.5 bg-slate-100 hover:bg-slate-200 border border-slate-200 text-slate-700 disabled:border-slate-100 disabled:text-slate-400 font-bold font-mono text-[11px] uppercase rounded transition-all flex items-center gap-1.5 cursor-pointer"
              >
                {isTesting ? (
                  <RefreshCw className="w-3.5 h-3.5 animate-spin text-red-500" />
                ) : (
                  <CheckCircle className="w-3.5 h-3.5 text-red-500" />
                )}
                {language === 'uk' ? 'Перевірити підключення' : 'Test Connection'}
              </button>
            </div>
          </div>

          {/* Test Results Output Panel */}
          {testResult.status !== 'idle' && (
            <div className={`p-4 rounded border font-mono text-[11px] leading-relaxed ${
              testResult.status === 'success' 
                ? 'bg-emerald-50 border-emerald-200 text-emerald-800' 
                : 'bg-red-50 border-red-200 text-red-800'
            }`}>
              <div className="flex gap-2.5 items-start">
                {testResult.status === 'success' ? (
                  <CheckCircle className="w-4.5 h-4.5 text-emerald-600 shrink-0 mt-0.5" />
                ) : (
                  <AlertTriangle className="w-4.5 h-4.5 text-red-600 shrink-0 mt-0.5" />
                )}
                <div className="space-y-1">
                  <span className="font-bold uppercase tracking-wider">
                    {testResult.status === 'success' 
                      ? (language === 'uk' ? 'Результат тесту: Успішно' : 'Test Result: Success')
                      : (language === 'uk' ? 'Результат тесту: Помилка' : 'Test Result: Failure')
                    }
                  </span>
                  <p>{testResult.message}</p>
                </div>
              </div>
            </div>
          )}

          {/* Guide Links */}
          <div className="pt-6 border-t border-slate-200 space-y-3">
            <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 font-mono flex items-center gap-1.5">
              <HelpCircle className="w-3.5 h-3.5" />
              {language === 'uk' ? 'Як отримати власний ключ API?' : 'How to get your personal API Key?'}
            </h4>
            
            <div className="text-[11px] font-mono leading-relaxed text-slate-600 space-y-2">
              <p>
                {language === 'uk' 
                  ? '1. Перейдіть до Google AI Studio (безкоштовно для розробників).' 
                  : '1. Navigate to Google AI Studio (completely free for developers and personal projects).'}
              </p>
              <p>
                {language === 'uk' 
                  ? '2. Натисніть кнопку "Get API Key" у верхньому лівому кутку.' 
                  : '2. Click the "Get API Key" button in the upper-left navigation.'}
              </p>
              <p>
                {language === 'uk' 
                  ? '3. Створіть новий ключ у новому або існуючому проекті Google Cloud.' 
                  : '3. Create a new key in a new or existing Google Cloud project.'}
              </p>
              
              <div className="pt-2">
                <a 
                  href="https://aistudio.google.com/" 
                  target="_blank" 
                  rel="noreferrer" 
                  className="inline-flex items-center gap-1 text-red-500 hover:text-red-600 font-bold uppercase text-[10px] transition-colors"
                >
                  <span>{language === 'uk' ? 'Відкрити Google AI Studio' : 'Open Google AI Studio'}</span>
                  <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </div>
          </div>
        </div>
      ) : activeSubTab === 'users' ? (
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-start gap-4 pb-6 border-b border-slate-200">
            <div className="p-3 bg-[#1E3A8A]/10 rounded border border-[#1E3A8A]/20 text-[#1E3A8A]">
              <Users className="w-6 h-6 animate-pulse" />
            </div>
            <div className="space-y-1">
              <h2 className="text-base font-bold font-mono uppercase tracking-wider text-slate-800">
                {language === 'uk' ? 'КЕРУВАННЯ КОРИСТУВАЧАМИ ТА БЕЗПЕКОЮ' : 'USER ACCESS & SECURITY CONTROLS'}
              </h2>
              <p className="text-[11px] text-slate-400 font-mono">
                {language === 'uk' 
                  ? 'СХВАЛЕННЯ ДОСТУПУ КОРИСТУВАЧІВ ТА НАЛАШТУВАННЯ ДВОФАКТОРНОЇ АВТЕНТИФІКАЦІЇ' 
                  : 'APPROVE USER LOGIN REQUESTS AND ENFORCE TWO-FACTOR AUTHENTICATION'}
              </p>
            </div>
          </div>

          {/* Info card */}
          <div className="p-4 bg-slate-50 border border-slate-200 rounded text-[11px] font-mono leading-relaxed text-slate-600 space-y-2">
            <p className="font-bold uppercase tracking-wider text-[#1E3A8A] flex items-center gap-1">
              <ShieldCheck className="w-4 h-4" />
              {language === 'uk' ? 'ПРАВИЛА ДОСТУПУ ЕКОСИСТЕМИ' : 'ECOSYSTEM ACCESS POLICIES'}
            </p>
            <p>
              {language === 'uk'
                ? 'Нові користувачі, які вперше авторизуються через Google, автоматично потрапляють у статус "Очікує схвалення" (якщо їх пошта не вказана у дозволених у файлі конфігурації). Адміністратор повинен підтвердити доступ для початку роботи користувача. Також адміністратор може ввімкнути вимогу додаткового пароля (як другого фактора) індивідуально для кожного користувача.'
                : 'New users logging in with Google for the first time will be put in "Pending Approval" state. The administrator must approve them before they can access any AI tools. You can also enforce a static password as a second factor individually.'}
            </p>
          </div>

          {/* Add New User by Email Form */}
          <div className="bg-white border border-slate-200 rounded-lg p-5 space-y-4 shadow-xs font-mono">
            <div className="flex items-center gap-2 text-slate-800 font-bold text-xs uppercase tracking-wide">
              <UserPlus className="w-4 h-4 text-emerald-600" />
              <span>{language === 'uk' ? 'ДОДАТИ НОВОГО КОРИСТУВАЧА ЗА EMAIL' : 'ADD NEW USER BY EMAIL'}</span>
            </div>
            <p className="text-[11px] text-slate-500 leading-relaxed">
              {language === 'uk'
                ? 'Введіть електронну пошту (email) користувача для надання йому доступу. Новий користувач буде доданий зі статусом СХВАЛЕНО (approved) і зможе одразу авторизуватися та користуватися аналітичною системою.'
                : 'Enter user email to grant access. The user will be created with APPROVED status and can immediately log in.'}
            </p>

            <form onSubmit={handleAddUser} className="space-y-3 pt-1">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-[10px] font-bold text-slate-600 uppercase">
                    Email *
                  </label>
                  <input
                    type="email"
                    required
                    placeholder="user@example.com"
                    value={newUserEmail}
                    onChange={(e) => setNewUserEmail(e.target.value)}
                    className="w-full bg-slate-50 border border-slate-200 rounded px-3 py-2 text-xs text-slate-800 font-mono focus:outline-none focus:border-[#1E3A8A]"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-[10px] font-bold text-slate-600 uppercase">
                    {language === 'uk' ? 'Ім\'я користувача (необов\'язково)' : 'Display Name (optional)'}
                  </label>
                  <input
                    type="text"
                    placeholder={language === 'uk' ? "Іван Іванов" : "John Doe"}
                    value={newUserName}
                    onChange={(e) => setNewUserName(e.target.value)}
                    className="w-full bg-slate-50 border border-slate-200 rounded px-3 py-2 text-xs text-slate-800 font-mono focus:outline-none focus:border-[#1E3A8A]"
                  />
                </div>
              </div>

              <div className="flex items-center justify-between gap-4 flex-wrap pt-1">
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={newUserUnlimited}
                    onChange={(e) => setNewUserUnlimited(e.target.checked)}
                    className="w-4 h-4 rounded border-slate-300 text-purple-600 focus:ring-purple-500 cursor-pointer"
                  />
                  <span className="text-xs font-bold text-slate-700 flex items-center gap-1">
                    <Sparkles className="w-3.5 h-3.5 text-purple-600" />
                    <span>{language === 'uk' ? 'Надати безлімітний доступ на ВСІ агенти' : 'Grant unlimited access to ALL agents'}</span>
                  </span>
                </label>

                <button
                  type="submit"
                  disabled={isAddingUser}
                  className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs uppercase rounded-lg transition-colors cursor-pointer shadow-xs flex items-center gap-2 disabled:opacity-50"
                >
                  <UserPlus className="w-4 h-4" />
                  <span>{isAddingUser ? (language === 'uk' ? 'ДОДАВАННЯ...' : 'ADDING...') : (language === 'uk' ? 'ДОДАТИ КОРИСТУВАЧА' : 'ADD USER')}</span>
                </button>
              </div>

              {addUserSuccess && (
                <div className="p-3 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded text-xs flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 shrink-0 text-emerald-600" />
                  <span>{addUserSuccess}</span>
                </div>
              )}

              {addUserError && (
                <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded text-xs flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 shrink-0 text-red-600" />
                  <span>{addUserError}</span>
                </div>
              )}
            </form>
          </div>

          {/* Search bar & Refresh */}
          <div className="flex gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder={language === 'uk' ? "Пошук за email або ім'ям..." : "Search by email or name..."}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded pl-10 pr-4 py-2.5 text-xs text-slate-800 font-mono focus:outline-none focus:border-[#1E3A8A]"
              />
            </div>
            <button
              onClick={fetchUsers}
              disabled={isLoadingUsers}
              className="px-3 bg-slate-100 hover:bg-slate-200 border border-slate-200 rounded text-slate-700 disabled:text-slate-400 cursor-pointer flex items-center gap-1"
              title="Оновити список"
            >
              <RefreshCw className={`w-4 h-4 ${isLoadingUsers ? 'animate-spin' : ''}`} />
            </button>
          </div>

          {/* User List Table */}
          {isLoadingUsers && users.length === 0 ? (
            <div className="flex flex-col items-center py-12 text-slate-400 font-mono text-xs gap-3">
              <RefreshCw className="w-6 h-6 animate-spin text-[#1E3A8A]" />
              <span>{language === 'uk' ? 'ЗАВАНТАЖЕННЯ СПИСКУ КОРИСТУВАЧІВ...' : 'LOADING USER LIST...'}</span>
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="p-8 border border-dashed border-slate-200 rounded text-center text-slate-400 font-mono text-xs">
              {language === 'uk' ? 'КОРИСТУВАЧІВ НЕ ЗНАЙДЕНО' : 'NO USERS FOUND'}
            </div>
          ) : (
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse font-mono text-xs">
                  <thead>
                    <tr className="bg-slate-50 text-slate-400 font-bold uppercase tracking-wider text-[10px] border-b border-slate-200">
                      <th className="p-4">{language === 'uk' ? 'Користувач' : 'User'}</th>
                      <th className="p-4">{language === 'uk' ? 'Роль' : 'Role'}</th>
                      <th className="p-4">{language === 'uk' ? 'Статус доступу' : 'Access Status'}</th>
                      <th className="p-4">{language === 'uk' ? 'Безлімітний доступ' : 'Unlimited Access'}</th>
                      <th className="p-4">{language === 'uk' ? 'Другий фактор' : '2FA Password'}</th>
                      <th className="p-4 text-right">{language === 'uk' ? 'Дії' : 'Actions'}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {filteredUsers.map(user => {
                      const isSelf = ['bbass776@gmail.com', 'irinacosmos777@gmail.com'].includes(user.email?.toLowerCase() || '');
                      const isUpdating = updatingUserEmail === user.email;

                      return (
                        <tr key={user.email} className={`hover:bg-slate-50/50 transition-colors ${isSelf ? 'bg-amber-50/20' : ''}`}>
                          {/* User info */}
                          <td className="p-4">
                            <div className="flex items-center gap-3">
                              {user.avatar ? (
                                <img src={user.avatar} alt={user.displayName} className="w-8 h-8 rounded-full border border-slate-200" referrerPolicy="no-referrer" />
                              ) : (
                                <div className="w-8 h-8 rounded-full bg-[#1E3A8A]/10 text-[#1E3A8A] flex items-center justify-center font-bold text-xs">
                                  {user.displayName.charAt(0).toUpperCase()}
                                </div>
                              )}
                              <div className="space-y-0.5">
                                <p className="font-semibold text-slate-800">{user.displayName}</p>
                                <p className="text-[10px] text-slate-400 flex items-center gap-1">
                                  <Mail className="w-3 h-3" />
                                  {user.email}
                                </p>
                              </div>
                            </div>
                          </td>

                          {/* Role */}
                          <td className="p-4">
                            <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${
                              user.role === 'admin' 
                                ? 'bg-red-50 text-red-600 border border-red-200' 
                                : 'bg-slate-100 text-slate-600 border border-slate-200'
                            }`}>
                              {user.role}
                            </span>
                          </td>

                          {/* Access status */}
                          <td className="p-4">
                            <div className="flex flex-col gap-1">
                              {user.status === 'approved' ? (
                                <span className="inline-flex items-center gap-1 text-emerald-600 font-bold">
                                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                                  {language === 'uk' ? 'СХВАЛЕНО' : 'APPROVED'}
                                </span>
                              ) : user.status === 'rejected' ? (
                                <span className="inline-flex items-center gap-1 text-red-500 font-bold">
                                  <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
                                  {language === 'uk' ? 'ЗАБЛОКОВАНО' : 'REJECTED'}
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 text-amber-500 font-bold animate-pulse">
                                  <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                                  {language === 'uk' ? 'ОЧІКУЄ СХВАЛЕННЯ' : 'PENDING'}
                                </span>
                              )}
                              <p className="text-[9px] text-slate-400 flex items-center gap-1 mt-0.5">
                                <Calendar className="w-2.5 h-2.5" />
                                {new Date(user.createdAt).toLocaleDateString()}
                              </p>
                            </div>
                          </td>

                          {/* Unlimited Access status */}
                          <td className="p-4">
                            <div className="flex flex-col gap-1">
                              {user.unlimitedAll ? (
                                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] font-extrabold uppercase rounded bg-purple-50 text-purple-700 border border-purple-200">
                                  <Sparkles className="w-3 h-3 text-purple-600 animate-pulse" />
                                  <span>{language === 'uk' ? 'Всі агенти' : 'All Agents'}</span>
                                </span>
                              ) : user.unlimitedAgents && user.unlimitedAgents.length > 0 ? (
                                <span className="inline-flex items-center gap-1.5 px-2 py-1 text-[10px] font-bold uppercase rounded bg-amber-50 text-amber-800 border border-amber-200">
                                  <Sparkles className="w-3 h-3 text-amber-600" />
                                  <span>{language === 'uk' ? `Вибрані (${user.unlimitedAgents.length})` : `Selected (${user.unlimitedAgents.length})`}</span>
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-semibold uppercase rounded bg-slate-100 text-slate-500 border border-slate-200">
                                  <span>{language === 'uk' ? 'За загальними правилами' : 'Standard Terms'}</span>
                                </span>
                              )}
                            </div>
                          </td>

                          {/* Password configuration */}
                          <td className="p-4">
                            <div className="flex flex-col gap-1.5">
                              <label className="flex items-center gap-2 cursor-pointer select-none">
                                <input
                                  type="checkbox"
                                  checked={user.twoFactorEnabled}
                                  disabled={isSelf || isUpdating}
                                  onChange={() => handleToggle2FA(user.email, user.twoFactorEnabled)}
                                  className="w-3.5 h-3.5 rounded border-slate-350 text-[#1E3A8A] focus:ring-[#1E3A8A] cursor-pointer"
                                />
                                <span className="font-semibold text-slate-700">
                                  {language === 'uk' ? 'Вимагати пароль' : 'Enforce Password'}
                                </span>
                              </label>
                              
                              <div className="flex items-center gap-1 text-[9px] text-slate-400">
                                {user.has2FASecret ? (
                                  <span className="text-green-600 font-bold flex items-center gap-0.5">
                                    <Lock className="w-2.5 h-2.5" />
                                    {language === 'uk' ? 'Пароль встановлено' : 'Password configured'}
                                  </span>
                                ) : (
                                  <span className="text-slate-400 flex items-center gap-0.5">
                                    <Unlock className="w-2.5 h-2.5" />
                                    {language === 'uk' ? 'Пароль не встановлено' : 'Password not set'}
                                  </span>
                                )}
                              </div>
                            </div>
                          </td>

                          {/* Action Buttons */}
                          <td className="p-4 text-right">
                            <div className="flex items-center justify-end gap-1.5">
                              {/* Unlimited access config button */}
                              <button
                                onClick={() => handleOpenUnlimitedModal(user)}
                                disabled={isUpdating}
                                className="px-2 py-1.5 bg-purple-50 hover:bg-purple-100 border border-purple-200 text-purple-700 rounded transition-all cursor-pointer flex items-center gap-1 text-[10px] font-bold"
                                title={language === 'uk' ? "Налаштувати безлімітний доступ" : "Configure unlimited access"}
                              >
                                <Sparkles className="w-3.5 h-3.5 text-purple-600" />
                                <span>{language === 'uk' ? 'Безліміт' : 'Unlimited'}</span>
                              </button>

                              {/* Status Buttons */}
                              {!isSelf && (
                                <>
                                  {user.status !== 'approved' && (
                                    <button
                                      onClick={() => handleUpdateStatus(user.email, 'approved')}
                                      disabled={isUpdating}
                                      className="p-1.5 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 text-emerald-600 rounded transition-all cursor-pointer"
                                      title={language === 'uk' ? "Схвалити доступ" : "Approve user"}
                                    >
                                      <UserCheck className="w-4 h-4" />
                                    </button>
                                  )}
                                  {user.status !== 'rejected' && (
                                    <button
                                      onClick={() => handleUpdateStatus(user.email, 'rejected')}
                                      disabled={isUpdating}
                                      className="p-1.5 bg-red-50 hover:bg-red-100 border border-red-200 text-red-500 rounded transition-all cursor-pointer"
                                      title={language === 'uk' ? "Заблокувати доступ" : "Reject/Block user"}
                                    >
                                      <UserX className="w-4 h-4" />
                                    </button>
                                  )}
                                </>
                              )}

                              {/* Reset Password */}
                              {user.has2FASecret && (
                                <button
                                  onClick={() => handleReset2FA(user.email)}
                                  disabled={isUpdating}
                                  className="p-1.5 bg-slate-100 hover:bg-slate-200 border border-slate-200 text-slate-600 rounded transition-all cursor-pointer text-[10px] font-bold"
                                  title={language === 'uk' ? "Скинути пароль" : "Reset Password"}
                                >
                                  {language === 'uk' ? 'Скинути пароль' : 'Reset Password'}
                                </button>
                              )}

                              {isSelf && (
                                <span className="text-[10px] text-slate-400 font-mono italic">
                                  {language === 'uk' ? 'Це ваш профіль' : 'Your profile'}
                                </span>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      ) : activeSubTab === 'pricing' ? (
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-start gap-4 pb-6 border-b border-slate-200">
            <div className="p-3 bg-[#1E3A8A]/10 rounded border border-[#1E3A8A]/20 text-[#1E3A8A]">
              <Lock className="w-6 h-6 animate-pulse" />
            </div>
            <div className="space-y-1">
              <h2 className="text-base font-bold font-mono uppercase tracking-wider text-slate-800">
                {language === 'uk' ? 'НАЛАШТУВАННЯ ТАРИФІВ & ПЛАТІЖНИХ СИСТЕМ' : 'PRICING & PAYMENT GATEWAYS'}
              </h2>
              <p className="text-[11px] text-slate-400 font-mono">
                {language === 'uk' 
                  ? 'ВСТАНОВЛЕННЯ ЦІН, ЛІМІТІВ ЗАПИТІВ ТА ТОКЕНА МОНОБАНКУ' 
                  : 'CONFIGURE AGENT PRICING, LIMITS, AND MONOBANK API INTEGRATION'}
              </p>
            </div>
          </div>

          {/* Monobank Token Settings */}
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 space-y-4">
            <h3 className="text-xs font-bold text-[#1E3A8A] uppercase tracking-wider flex items-center gap-1.5">
              <span>💳</span>
              {language === 'uk' ? 'Налаштування Monobank Acquiring' : 'Monobank Acquiring Settings'}
            </h3>
            
            <div className="space-y-2">
              <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400 font-mono">
                {language === 'uk' ? 'Monobank Merchant Token (X-Token)' : 'Monobank Merchant Token (X-Token)'}
              </label>
              
              <div className="flex gap-3">
                <input
                  type="password"
                  value={monobankToken}
                  onChange={(e) => setMonobankToken(e.target.value)}
                  placeholder="X-Token з кабінету FOP монобанку..."
                  className="flex-1 bg-white border border-slate-200 rounded px-4 py-2 text-xs text-slate-800 font-mono focus:outline-none focus:border-[#1E3A8A]"
                />
                <button
                  onClick={handleSaveMonobankToken}
                  disabled={isSavingToken}
                  className="px-4 py-2 bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 disabled:bg-slate-100 disabled:text-slate-400 text-white font-bold font-mono text-[11px] uppercase rounded transition-all cursor-pointer flex items-center gap-1"
                >
                  {isSavingToken ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : null}
                  <span>{language === 'uk' ? 'Зберегти токен' : 'Save Token'}</span>
                </button>
                <button
                  type="button"
                  onClick={handleTestMonobank}
                  disabled={isTestingMono || !monobankToken}
                  className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-100 disabled:text-slate-400 text-white font-bold font-mono text-[11px] uppercase rounded transition-all cursor-pointer flex items-center gap-1"
                >
                  {isTestingMono ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Activity className="w-3.5 h-3.5" />}
                  <span>{language === 'uk' ? 'Перевірити статус' : 'Test Status'}</span>
                </button>
              </div>
              <p className="text-[9px] text-slate-400 leading-normal font-mono">
                {language === 'uk'
                  ? 'Цей токен необхідний для автоматичного створення рахунків в Monobank API. Ви можете згенерувати його у кабінеті web.monobank.ua або pay.monobank.ua.'
                  : 'This token is required to programmatically issue invoices using Monobank API. Generate it at web.monobank.ua or pay.monobank.ua.'}
              </p>

              {monoTestResult && (
                <div className={`p-3.5 rounded-lg border text-xs font-mono flex flex-col gap-2 ${
                  monoTestResult.ok
                    ? 'bg-emerald-50 border-emerald-300 text-emerald-800'
                    : monoTestResult.isTerminalMissing
                    ? 'bg-amber-50 border-amber-300 text-amber-900'
                    : 'bg-rose-50 border-rose-300 text-rose-800'
                }`}>
                  <div className="flex items-center gap-2 font-bold">
                    <span>{monoTestResult.ok ? '✅' : monoTestResult.isTerminalMissing ? '⚠️' : '❌'}</span>
                    <span>{monoTestResult.ok ? (language === 'uk' ? 'Еквайринг активний' : 'Acquiring Active') : monoTestResult.isTerminalMissing ? (language === 'uk' ? 'Термінал не активовано в Monobank' : 'Terminal Inactive in Monobank') : (language === 'uk' ? 'Помилка перевірки токена' : 'Token Verification Error')}</span>
                  </div>
                  <p className="leading-relaxed text-[11px]">
                    {monoTestResult.message}
                  </p>
                  {monoTestResult.isTerminalMissing && (
                    <div className="mt-1 pt-2 border-t border-amber-200 text-[10px] space-y-1 text-slate-700">
                      <div className="font-bold text-amber-900">Покрокова інструкція для активації Plata by mono:</div>
                      <div>1. Увійдіть у кабінет <a href="https://web.monobank.ua/" target="_blank" rel="noreferrer" className="text-blue-600 underline font-bold">web.monobank.ua</a> (або відкрийте додаток Monobank).</div>
                      <div>2. Перейдіть у розділ <b>«Еквайринг»</b> → <b>«Інтернет-еквайринг»</b> (Plata by mono).</div>
                      <div>3. Подайте заявку на підключення сайту/сервісу (вказавши посилання <code>https://deepresearchhub.online</code>).</div>
                      <div>4. Після перевірки та активації терміналу банком перевірте X-Token цього активного інтернет-терміналу та збережіть його в цьому полі.</div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Pricing Settings Table */}
          {isLoadingPricing ? (
            <div className="flex flex-col items-center py-12 text-slate-400 font-mono text-xs gap-3">
              <RefreshCw className="w-6 h-6 animate-spin text-[#1E3A8A]" />
              <span>{language === 'uk' ? 'ЗАВАНТАЖЕННЯ НАЛАШТУВАНЬ ТАРИФІВ...' : 'LOADING PRICING SETTINGS...'}</span>
            </div>
          ) : (
            <div className="space-y-4">
              <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider font-mono">
                {language === 'uk' ? 'Тарифи Інструментів' : 'AI Agent Tariffs'}
              </h3>
              
              <div className="border border-slate-200 rounded-lg overflow-x-auto touch-pan-x">
                <table className="w-full text-left border-collapse font-mono text-xs min-w-[720px]">
                  <thead>
                    <tr className="bg-slate-50 text-slate-400 font-bold uppercase tracking-wider text-[10px] border-b border-slate-200">
                      <th className="p-4">{language === 'uk' ? 'Агент' : 'Agent'}</th>
                      <th className="p-4">{language === 'uk' ? 'Ліміт запитів' : 'Request Limit'}</th>
                      <th className="p-4">{language === 'uk' ? 'Підписка (грн)' : 'Subscription (UAH)'}</th>
                      <th className="p-4">{language === 'uk' ? 'Разово (грн)' : 'Single Run (UAH)'}</th>
                      <th className="p-4">{language === 'uk' ? 'Термін дії (днів)' : 'Duration (days)'}</th>
                      <th className="p-4 text-right">{language === 'uk' ? 'Дії' : 'Actions'}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {agentSettings.map(settings => {
                      const isSaving = savingAgentId === settings.agent_id;
                      
                      return (
                        <tr key={settings.agent_id} className="hover:bg-slate-50/50 transition-colors">
                          <td className="p-4">
                            <span className="font-bold text-slate-800">{getAgentName(settings.agent_id)}</span>
                            <span className="block text-[9px] text-slate-400 uppercase mt-0.5">{settings.agent_id}</span>
                          </td>
                          <td className="p-4">
                            <input
                              type="number"
                              defaultValue={settings.requests_limit}
                              id={`limit_${settings.agent_id}`}
                              onChange={() => updateAutoSinglePrice(settings.agent_id)}
                              className="w-24 bg-slate-50 border border-slate-200 rounded px-3 py-1.5 font-bold focus:outline-none focus:border-[#1E3A8A]"
                            />
                          </td>
                          <td className="p-4">
                            <input
                              type="number"
                              step="0.01"
                              defaultValue={settings.price}
                              id={`price_${settings.agent_id}`}
                              onChange={() => updateAutoSinglePrice(settings.agent_id)}
                              className="w-24 bg-slate-50 border border-slate-200 rounded px-3 py-1.5 font-bold focus:outline-none focus:border-[#1E3A8A]"
                            />
                          </td>
                          <td className="p-4">
                            <input
                              type="number"
                              step="0.01"
                              defaultValue={settings.single_price !== undefined && settings.single_price !== null ? settings.single_price : 0}
                              id={`single_price_${settings.agent_id}`}
                              className="w-24 bg-slate-50 border border-slate-200 rounded px-3 py-1.5 font-bold focus:outline-none focus:border-[#1E3A8A]"
                            />
                          </td>
                          <td className="p-4">
                            <input
                              type="number"
                              defaultValue={settings.duration_days !== undefined && settings.duration_days !== null ? settings.duration_days : 30}
                              id={`duration_${settings.agent_id}`}
                              className="w-24 bg-slate-50 border border-slate-200 rounded px-3 py-1.5 font-bold focus:outline-none focus:border-[#1E3A8A]"
                            />
                          </td>
                          <td className="p-4 text-right">
                            <button
                              onClick={() => {
                                const limitInput = document.getElementById(`limit_${settings.agent_id}`) as HTMLInputElement;
                                const priceInput = document.getElementById(`price_${settings.agent_id}`) as HTMLInputElement;
                                const singlePriceInput = document.getElementById(`single_price_${settings.agent_id}`) as HTMLInputElement;
                                const durationInput = document.getElementById(`duration_${settings.agent_id}`) as HTMLInputElement;
                                if (limitInput && priceInput && singlePriceInput && durationInput) {
                                  handleSaveAgentSettings(
                                    settings.agent_id,
                                    parseInt(limitInput.value),
                                    parseFloat(priceInput.value),
                                    parseInt(durationInput.value),
                                    parseFloat(singlePriceInput.value)
                                  );
                                }
                              }}
                              disabled={isSaving}
                              className="px-3.5 py-1.5 bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 disabled:bg-slate-100 disabled:text-slate-400 text-white font-bold font-mono text-[10px] uppercase rounded transition-all cursor-pointer flex items-center gap-1.5 ml-auto"
                            >
                              {isSaving ? <RefreshCw className="w-3 h-3 animate-spin" /> : null}
                              <span>{language === 'uk' ? 'Зберегти' : 'Save'}</span>
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      ) : activeSubTab === 'satellite' ? (
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-start gap-4 pb-6 border-b border-slate-200">
            <div className="p-3 bg-blue-50 rounded border border-blue-200 text-[#1E3A8A]">
              <Globe className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-base font-bold text-slate-900 uppercase font-mono">
                {language === 'uk' ? '🛰️ КЛЮЧІ ТА ДОСТУП ДО КОМЕРЦІЙНИХ СУПУТНИКІВ' : '🛰️ SATELLITE API CREDENTIALS'}
              </h2>
              <p className="text-xs text-slate-500 font-mono mt-1">
                {language === 'uk'
                  ? 'Налаштування реквізитів авторизації для замовлення знімків надвисокої роздільності (Maxar WorldView-3, Planet SkySat, Airbus Pléiades, ICEYE SAR) через агрегатор UP42 STAC API v2.'
                  : 'Configure authorization credentials for requesting Very High Resolution imagery (Maxar, SkySat, Airbus, ICEYE) via UP42 STAC API v2.'}
              </p>
            </div>
          </div>

          {isLoadingSatellite ? (
            <div className="flex flex-col items-center py-12 text-slate-400 font-mono text-xs gap-3">
              <RefreshCw className="w-6 h-6 animate-spin text-[#1E3A8A]" />
              <span>{language === 'uk' ? 'ЗАВАНТАЖЕННЯ НАЛАШТУВАНЬ СУПУТНИКОВИХ API...' : 'LOADING SATELLITE API SETTINGS...'}</span>
            </div>
          ) : (
            <div className="space-y-6">

              {/* UP42 Aggregator Credentials Card */}
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-5 space-y-4 shadow-sm">
                <div className="flex items-center justify-between">
                  <h3 className="text-xs font-bold text-slate-800 uppercase font-mono flex items-center gap-2">
                    <Globe className="w-4 h-4 text-[#1E3A8A]" />
                    <span>{language === 'uk' ? 'Агрегатор UP42 STAC API v2 (Рекомендовано)' : 'UP42 STAC API v2 Aggregator (Recommended)'}</span>
                  </h3>
                  <span className="px-2 py-0.5 bg-blue-100 text-blue-800 text-[10px] font-mono font-bold rounded">
                    Maxar • SkySat • Airbus • ICEYE
                  </span>
                </div>

                <p className="text-[11px] text-slate-500 font-mono leading-relaxed">
                  {language === 'uk'
                    ? 'Агрегатор UP42 дає єдиний доступ до всіх комерційних супутникових систем без річних контрактів. Зареєструйтесь на console.up42.com, створіть Project та скопіюйте Project ID і Project Secret.'
                    : 'UP42 aggregates Maxar, Planet, Airbus, and ICEYE under a single pay-as-you-go API. Obtain Project ID and Secret at console.up42.com.'}
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-bold text-slate-600 uppercase font-mono">
                      UP42 Project ID
                    </label>
                    <input
                      type="text"
                      value={up42ProjectId}
                      onChange={(e) => setUp42ProjectId(e.target.value)}
                      placeholder="e.g. d7f8a9b0-1234-5678-9abc-def012345678"
                      className="w-full bg-white border border-slate-200 rounded px-3 py-2 text-xs font-mono text-slate-800 focus:outline-none focus:border-[#1E3A8A]"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-[10px] font-bold text-slate-600 uppercase font-mono">
                      UP42 Project Secret
                    </label>
                    <div className="relative">
                      <input
                        type={showUp42Secret ? 'text' : 'password'}
                        value={up42ProjectSecret}
                        onChange={(e) => setUp42ProjectSecret(e.target.value)}
                        placeholder="secret_..."
                        className="w-full bg-white border border-slate-200 rounded px-3 py-2 text-xs font-mono text-slate-800 focus:outline-none focus:border-[#1E3A8A] pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowUp42Secret(!showUp42Secret)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 cursor-pointer"
                      >
                        {showUp42Secret ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                      </button>
                    </div>
                  </div>
                </div>

                <div className="space-y-1.5 pt-1">
                  <label className="text-[10px] font-bold text-slate-600 uppercase font-mono">
                    {language === 'uk' ? 'Середовище виконання (Environment)' : 'API Environment'}
                  </label>
                  <select
                    value={up42Env}
                    onChange={(e) => setUp42Env(e.target.value as any)}
                    className="w-full md:w-64 bg-white border border-slate-200 rounded px-3 py-2 text-xs font-mono text-slate-800 focus:outline-none focus:border-[#1E3A8A]"
                  >
                    <option value="production">Production (https://api.up42.com)</option>
                    <option value="staging">Staging / Test (https://api-staging.up42.com)</option>
                  </select>
                </div>
              </div>

              {/* Direct Provider Keys Card (Optional) */}
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-5 space-y-4 shadow-sm">
                <h3 className="text-xs font-bold text-slate-800 uppercase font-mono flex items-center gap-2">
                  <Key className="w-4 h-4 text-slate-600" />
                  <span>{language === 'uk' ? 'Прямі API-ключі провайдерів (Опціонально)' : 'Direct Provider API Keys (Optional)'}</span>
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-bold text-slate-600 uppercase font-mono">
                      Planet Labs API Key
                    </label>
                    <div className="relative">
                      <input
                        type={showPlanetKey ? 'text' : 'password'}
                        value={planetApiKey}
                        onChange={(e) => setPlanetApiKey(e.target.value)}
                        placeholder="PLAK..."
                        className="w-full bg-white border border-slate-200 rounded px-3 py-2 text-xs font-mono text-slate-800 focus:outline-none focus:border-[#1E3A8A] pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPlanetKey(!showPlanetKey)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 cursor-pointer"
                      >
                        {showPlanetKey ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                      </button>
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-[10px] font-bold text-slate-600 uppercase font-mono">
                      Maxar ARD API Token
                    </label>
                    <input
                      type="text"
                      value={maxarApiToken}
                      onChange={(e) => setMaxarApiToken(e.target.value)}
                      placeholder="Bearer token..."
                      className="w-full bg-white border border-slate-200 rounded px-3 py-2 text-xs font-mono text-slate-800 focus:outline-none focus:border-[#1E3A8A]"
                    />
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap items-center gap-3 pt-2">
                <button
                  onClick={handleSaveSatelliteSettings}
                  disabled={isSavingSatellite}
                  className="px-6 py-2.5 bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 disabled:bg-slate-100 disabled:text-slate-400 text-white font-bold font-mono text-[11px] uppercase rounded transition-all cursor-pointer flex items-center gap-2 shadow-sm"
                >
                  {isSavingSatellite ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Globe className="w-3.5 h-3.5" />}
                  <span>{language === 'uk' ? 'Зберегти ключі' : 'Save Credentials'}</span>
                </button>

                <button
                  onClick={handleTestUp42Connection}
                  disabled={isTestingUp42 || !up42ProjectId || !up42ProjectSecret}
                  className="px-5 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-800 disabled:opacity-50 font-bold font-mono text-[11px] uppercase rounded transition-all cursor-pointer flex items-center gap-2 border border-slate-200"
                >
                  {isTestingUp42 ? <RefreshCw className="w-3.5 h-3.5 animate-spin text-blue-600" /> : <CheckCircle className="w-3.5 h-3.5 text-emerald-600" />}
                  <span>{language === 'uk' ? 'Перевірити підключення до UP42' : 'Test UP42 Connection'}</span>
                </button>
              </div>

              {/* Save Status Result */}
              {satelliteSaveResult.status !== 'idle' && (
                <div className={`p-4 rounded border font-mono text-xs ${
                  satelliteSaveResult.status === 'success'
                    ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                    : 'bg-rose-50 border-rose-200 text-rose-800'
                }`}>
                  <div className="flex items-center gap-2">
                    {satelliteSaveResult.status === 'success' ? (
                      <CheckCircle className="w-4 h-4 text-emerald-600 shrink-0" />
                    ) : (
                      <AlertTriangle className="w-4 h-4 text-rose-600 shrink-0" />
                    )}
                    <span>{satelliteSaveResult.message}</span>
                  </div>
                </div>
              )}

              {/* Test Status Result */}
              {up42TestResult.status !== 'idle' && (
                <div className={`p-4 rounded border font-mono text-xs ${
                  up42TestResult.status === 'success'
                    ? 'bg-blue-50 border-blue-200 text-blue-900'
                    : 'bg-amber-50 border-amber-200 text-amber-900'
                }`}>
                  <div className="flex items-center gap-2">
                    {up42TestResult.status === 'success' ? (
                      <CheckCircle className="w-4 h-4 text-blue-600 shrink-0" />
                    ) : (
                      <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
                    )}
                    <span>{up42TestResult.message}</span>
                  </div>
                </div>
              )}

            </div>
          )}
        </div>
      ) : activeSubTab === 'ncuvkz' ? (
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-start gap-4 pb-6 border-b border-slate-200">
            <div className="p-3 bg-emerald-50 rounded border border-emerald-200 text-emerald-800">
              <Activity className="w-6 h-6 animate-pulse" />
            </div>
            <div>
              <h2 className="text-base font-bold text-slate-900 uppercase font-mono flex items-center gap-2">
                <span>🛰️ ШЛЮЗИ НЦУВКЗ ДКАУ ТА СИСТЕМИ «НІС-ЕКО»</span>
                <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 text-[10px] rounded">Data Fusion 24/7</span>
              </h2>
              <p className="text-xs text-slate-500 font-mono mt-1 leading-relaxed">
                {language === 'uk'
                  ? 'Конфігурація цифрових інтерфейсів взаємодії з Національним центром управління та випробувань космічних засобів (Дзеркало Copernicus STAC API, 79 ГНСС-станцій СКНЗУ NTRIP, сейсмоакустика ГЦСК SeedLink 18000) та налаштування цілодобового фонового демона.'
                  : 'Configuration of NCUVKZ gateways (Copernicus Mirror STAC API, 79 GNSS SKNZU NTRIP, GCSK SeedLink 18000) and 24/7 background monitoring daemon.'}
              </p>
            </div>
          </div>

          {isLoadingNcuvkz ? (
            <div className="flex flex-col items-center py-12 text-slate-400 font-mono text-xs gap-3">
              <RefreshCw className="w-6 h-6 animate-spin text-emerald-700" />
              <span>{language === 'uk' ? 'ЗАВАНТАЖЕННЯ НАЛАШТУВАНЬ ШЛЮЗІВ НЦУВКЗ...' : 'LOADING NCUVKZ GATEWAY SETTINGS...'}</span>
            </div>
          ) : (
            <div className="space-y-6">
              
              {/* Card 1: Copernicus Mirror Site */}
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-5 space-y-4 shadow-sm">
                <div className="flex items-center justify-between">
                  <h3 className="text-xs font-bold text-slate-800 uppercase font-mono flex items-center gap-2">
                    <Globe className="w-4 h-4 text-emerald-700" />
                    <span>1. Національний дзеркальний сайт Copernicus (НЦУВКЗ)</span>
                  </h3>
                  <span className="px-2 py-0.5 bg-blue-100 text-blue-800 text-[10px] font-mono font-bold rounded">
                    Sentinel-1 SAR • Sentinel-2 MSI • Sentinel-5P
                  </span>
                </div>
                <p className="text-[11px] text-slate-500 font-mono leading-relaxed">
                  Прямий швидкісний доступ до дзеркала супутникових знімків України без обмежень європейських квот через STAC та OData REST API.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-1">
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-bold text-slate-600 uppercase font-mono">
                      STAC / OData API Endpoint URL
                    </label>
                    <input
                      type="text"
                      value={copernicusMirrorUrl}
                      onChange={(e) => setCopernicusMirrorUrl(e.target.value)}
                      placeholder="https://copernicus.spacecenter.gov.ua/api/stac"
                      className="w-full bg-white border border-slate-200 rounded px-3 py-2 text-xs font-mono text-slate-800 focus:outline-none focus:border-emerald-600"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-bold text-slate-600 uppercase font-mono">
                      Service Account API Key / Token
                    </label>
                    <input
                      type="password"
                      value={copernicusApiKey}
                      onChange={(e) => setCopernicusApiKey(e.target.value)}
                      placeholder="Вставте токен, наданий фахівцями НЦУВКЗ"
                      className="w-full bg-white border border-slate-200 rounded px-3 py-2 text-xs font-mono text-slate-800 focus:outline-none focus:border-emerald-600"
                    />
                  </div>
                  <div className="space-y-1.5 md:col-span-2">
                    <label className="text-[10px] font-bold text-slate-600 uppercase font-mono">
                      Локальне S3 Сховище / Mirror Cache URI
                    </label>
                    <input
                      type="text"
                      value={copernicusS3Bucket}
                      onChange={(e) => setCopernicusS3Bucket(e.target.value)}
                      placeholder="s3://copernicus-mirror-ua/sentinel/"
                      className="w-full bg-white border border-slate-200 rounded px-3 py-2 text-xs font-mono text-slate-800 focus:outline-none focus:border-emerald-600"
                    />
                  </div>
                </div>
              </div>

              {/* Card 2: SKNZU GNSS Network */}
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-5 space-y-4 shadow-sm">
                <div className="flex items-center justify-between">
                  <h3 className="text-xs font-bold text-slate-800 uppercase font-mono flex items-center gap-2">
                    <Activity className="w-4 h-4 text-emerald-700" />
                    <span>2. Мережа ГНСС СКНЗУ («NET.Spacecenter» 79 станцій)</span>
                  </h3>
                  <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 text-[10px] font-mono font-bold rounded">
                    NTRIP RTCM 3.2 • RINEX 3.04 • ZTD/PWV
                  </span>
                </div>
                <p className="text-[11px] text-slate-500 font-mono leading-relaxed">
                  Потоковий прийом поправок та апостеріорних даних 79 базових станцій для усунення тропосферних спотворень у радарах InSAR.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-1">
                  <div className="space-y-1.5 md:col-span-2">
                    <label className="text-[10px] font-bold text-slate-600 uppercase font-mono">
                      NTRIP Caster Host
                    </label>
                    <input
                      type="text"
                      value={ntripCasterHost}
                      onChange={(e) => setNtripCasterHost(e.target.value)}
                      placeholder="ntrip.spacecenter.gov.ua"
                      className="w-full bg-white border border-slate-200 rounded px-3 py-2 text-xs font-mono text-slate-800 focus:outline-none focus:border-emerald-600"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-bold text-slate-600 uppercase font-mono">
                      NTRIP Caster Port
                    </label>
                    <input
                      type="text"
                      value={ntripCasterPort}
                      onChange={(e) => setNtripCasterPort(e.target.value)}
                      placeholder="2101"
                      className="w-full bg-white border border-slate-200 rounded px-3 py-2 text-xs font-mono text-slate-800 focus:outline-none focus:border-emerald-600"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-bold text-slate-600 uppercase font-mono">
                      NTRIP Логін (User)
                    </label>
                    <input
                      type="text"
                      value={ntripUser}
                      onChange={(e) => setNtripUser(e.target.value)}
                      placeholder="nis_eco_client"
                      className="w-full bg-white border border-slate-200 rounded px-3 py-2 text-xs font-mono text-slate-800 focus:outline-none focus:border-emerald-600"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-bold text-slate-600 uppercase font-mono">
                      NTRIP Пароль
                    </label>
                    <input
                      type="password"
                      value={ntripPassword}
                      onChange={(e) => setNtripPassword(e.target.value)}
                      placeholder="••••••••••••"
                      className="w-full bg-white border border-slate-200 rounded px-3 py-2 text-xs font-mono text-slate-800 focus:outline-none focus:border-emerald-600"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-bold text-slate-600 uppercase font-mono">
                      Точки Монтування (Mountpoints)
                    </label>
                    <input
                      type="text"
                      value={ntripMountpoints}
                      onChange={(e) => setNtripMountpoints(e.target.value)}
                      placeholder="DNIP00UKR0,ZAPO00UKR0,KHER00UKR0"
                      className="w-full bg-white border border-slate-200 rounded px-3 py-2 text-xs font-mono text-slate-800 focus:outline-none focus:border-emerald-600"
                    />
                  </div>
                  <div className="space-y-1.5 md:col-span-3">
                    <label className="text-[10px] font-bold text-slate-600 uppercase font-mono">
                      FTP/HTTP Репозиторій файлів RINEX 3.04 та рядів ZTD/PWV
                    </label>
                    <input
                      type="text"
                      value={sknzuRinexFtp}
                      onChange={(e) => setSknzuRinexFtp(e.target.value)}
                      placeholder="ftp://rinex.spacecenter.gov.ua/pub/rinex/"
                      className="w-full bg-white border border-slate-200 rounded px-3 py-2 text-xs font-mono text-slate-800 focus:outline-none focus:border-emerald-600"
                    />
                  </div>
                </div>
              </div>

              {/* Card 3: GCSK Special Control */}
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-5 space-y-4 shadow-sm">
                <div className="flex items-center justify-between">
                  <h3 className="text-xs font-bold text-slate-800 uppercase font-mono flex items-center gap-2">
                    <Activity className="w-4 h-4 text-amber-600" />
                    <span>3. Головний центр спеціального контролю (ГЦСК ДКАУ)</span>
                  </h3>
                  <span className="px-2 py-0.5 bg-amber-100 text-amber-800 text-[10px] font-mono font-bold rounded">
                    SeedLink MiniSEED (Порт 18000) • Сейсмоакустика • Бюлетені
                  </span>
                </div>
                <p className="text-[11px] text-slate-500 font-mono leading-relaxed">
                  Потоковий прийом сейсмоакустичних хвиль та інфразвуку (ПС-45 Малин, Балта тощо) для фіксації підривів гребель, вибухів і землетрусів.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-1">
                  <div className="space-y-1.5 md:col-span-2">
                    <label className="text-[10px] font-bold text-slate-600 uppercase font-mono">
                      SeedLink Server Host
                    </label>
                    <input
                      type="text"
                      value={seedlinkHost}
                      onChange={(e) => setSeedlinkHost(e.target.value)}
                      placeholder="seedlink.gcsk.gov.ua"
                      className="w-full bg-white border border-slate-200 rounded px-3 py-2 text-xs font-mono text-slate-800 focus:outline-none focus:border-amber-600"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-bold text-slate-600 uppercase font-mono">
                      SeedLink Port (За замовчуванням 18000)
                    </label>
                    <input
                      type="text"
                      value={seedlinkPort}
                      onChange={(e) => setSeedlinkPort(e.target.value)}
                      placeholder="18000"
                      className="w-full bg-white border border-slate-200 rounded px-3 py-2 text-xs font-mono text-slate-800 focus:outline-none focus:border-amber-600"
                    />
                  </div>
                  <div className="space-y-1.5 md:col-span-3">
                    <label className="text-[10px] font-bold text-slate-600 uppercase font-mono">
                      Список Дозволених Каналів (NET.STA.LOC.CHA)
                    </label>
                    <input
                      type="text"
                      value={seedlinkChannels}
                      onChange={(e) => setSeedlinkChannels(e.target.value)}
                      placeholder="PS45.MALYN.HHZ,BALT.HHZ,UZHG.HHZ"
                      className="w-full bg-white border border-slate-200 rounded px-3 py-2 text-xs font-mono text-slate-800 focus:outline-none focus:border-amber-600"
                    />
                  </div>
                </div>
              </div>

              {/* Card 4: 24/7 Autonomous Daemon & Telegram Alerts */}
              <div className="bg-slate-900 border border-emerald-800 rounded-lg p-5 space-y-4 shadow text-white">
                <div className="flex items-center justify-between">
                  <h3 className="text-xs font-bold text-emerald-400 uppercase font-mono flex items-center gap-2">
                    <RefreshCw className="w-4 h-4 animate-spin text-emerald-400" />
                    <span>4. Цілодобовий Автономний Режим 24/7 & Сповіщення Оператора</span>
                  </h3>
                  <div className="flex items-center gap-2">
                    <span className={`text-[10px] font-mono font-bold px-2 py-0.5 rounded ${ncuvkzDaemonEnabled ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30' : 'bg-slate-700 text-slate-400'}`}>
                      {ncuvkzDaemonEnabled ? '🟢 24/7 АКТИВНО' : '⚪ ВИМКНЕНО'}
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2">
                  <div className="flex items-center gap-3 bg-slate-800/80 p-3 rounded border border-slate-700">
                    <input
                      type="checkbox"
                      id="ncuvkz_daemon_toggle"
                      checked={ncuvkzDaemonEnabled}
                      onChange={(e) => setNcuvkzDaemonEnabled(e.target.checked)}
                      className="w-4 h-4 accent-emerald-500 rounded cursor-pointer"
                    />
                    <label htmlFor="ncuvkz_daemon_toggle" className="text-xs font-mono cursor-pointer select-none">
                      <span className="font-bold block text-emerald-300">Фоновий моніторинг 24/7</span>
                      <span className="text-[10px] text-slate-400">Автономний PM2 процес на сервері</span>
                    </label>
                  </div>

                  <div className="space-y-1 bg-slate-800/80 p-3 rounded border border-slate-700">
                    <label className="text-[10px] font-bold text-slate-300 uppercase font-mono">
                      Інтервал опитування шлюзів
                    </label>
                    <select
                      value={ncuvkzDaemonInterval}
                      onChange={(e) => setNcuvkzDaemonInterval(e.target.value)}
                      className="w-full bg-slate-900 border border-slate-600 rounded px-2.5 py-1.5 text-xs font-mono text-emerald-300 focus:outline-none focus:border-emerald-500"
                    >
                      <option value="5">Кожні 5 хвилин (Екстрений)</option>
                      <option value="15">Кожні 15 хвилин (Стандартний)</option>
                      <option value="30">Кожні 30 хвилин</option>
                      <option value="60">Щогодини (1 год)</option>
                      <option value="360">Кожні 6 годин</option>
                    </select>
                  </div>

                  <div className="space-y-1 bg-slate-800/80 p-3 rounded border border-slate-700">
                    <label className="text-[10px] font-bold text-slate-300 uppercase font-mono">
                      Telegram Chat ID оператора для алерту
                    </label>
                    <input
                      type="text"
                      value={ncuvkzOperatorChatId}
                      onChange={(e) => setNcuvkzOperatorChatId(e.target.value)}
                      placeholder="1421638496"
                      className="w-full bg-slate-900 border border-slate-600 rounded px-2.5 py-1.5 text-xs font-mono text-white focus:outline-none focus:border-emerald-500"
                    />
                  </div>
                </div>

                <div className="flex flex-wrap items-center justify-between gap-3 pt-3 border-t border-slate-800">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={handleSaveNcuvkzSettings}
                      disabled={isSavingNcuvkz}
                      className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-mono font-bold rounded shadow transition-all flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
                    >
                      {isSavingNcuvkz ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                      <span>{isSavingNcuvkz ? 'ЗБЕРЕЖЕННЯ...' : '💾 ЗБЕРЕГТИ НАЛАШТУВАННЯ'}</span>
                    </button>
                    
                    <button
                      onClick={handleTestNcuvkzConnection}
                      disabled={isTestingNcuvkz}
                      className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-emerald-300 border border-emerald-700/50 text-xs font-mono font-bold rounded transition-all flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
                    >
                      {isTestingNcuvkz ? <RefreshCw className="w-3.5 h-3.5 animate-spin text-emerald-400" /> : <Activity className="w-3.5 h-3.5 text-emerald-400" />}
                      <span>{isTestingNcuvkz ? 'ПЕРЕВІРКА ШЛЮЗІВ...' : '⚡ ТЕСТ ШЛЮЗІВ НЦУВКЗ'}</span>
                    </button>
                  </div>

                  <button
                    onClick={handlePingOperatorTelegram}
                    disabled={isPingingTelegram}
                    className="px-3 py-2 bg-blue-600/30 hover:bg-blue-600/50 text-blue-300 border border-blue-500/40 text-xs font-mono font-bold rounded transition-all flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
                  >
                    {isPingingTelegram ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                    <span>✈️ ПІНГ-ТЕСТ У TELEGRAM ОПЕРАТОРА</span>
                  </button>
                </div>

                {/* Save Feedback */}
                {ncuvkzSaveResult.status !== 'idle' && (
                  <div className={`p-3 rounded text-xs font-mono border ${
                    ncuvkzSaveResult.status === 'success' ? 'bg-emerald-950/80 border-emerald-500 text-emerald-200' : 'bg-rose-950/80 border-rose-500 text-rose-200'
                  }`}>
                    {ncuvkzSaveResult.message}
                  </div>
                )}

                {/* Test Feedback */}
                {ncuvkzTestResult.status !== 'idle' && (
                  <div className={`p-4 rounded-xl text-xs font-mono border space-y-4 shadow-lg ${
                    ncuvkzTestResult.status === 'success' 
                      ? 'bg-emerald-950/90 border-emerald-500 text-emerald-100' 
                      : ncuvkzTestResult.status === 'warning'
                      ? 'bg-slate-900 border-amber-500/80 text-slate-100'
                      : 'bg-rose-950/90 border-rose-500 text-rose-100'
                  }`}>
                    {/* Main Status Headline */}
                    <div className="flex items-start gap-2.5 pb-3 border-b border-slate-700">
                      {ncuvkzTestResult.status === 'success' ? (
                        <CheckCircle className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
                      ) : ncuvkzTestResult.status === 'warning' ? (
                        <AlertCircle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
                      ) : (
                        <AlertTriangle className="w-5 h-5 text-rose-400 shrink-0 mt-0.5" />
                      )}
                      <div className="space-y-1">
                        <div className="font-bold text-sm text-white">{ncuvkzTestResult.message}</div>
                        {ncuvkzTestResult.serverIp && (
                          <div className="text-[11px] text-slate-300">
                            🌐 Зовнішня IP-адреса нашого сервера для білого списку НЦУВКЗ: <code className="px-1.5 py-0.5 bg-slate-800 text-amber-300 rounded font-bold">{ncuvkzTestResult.serverIp}</code>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Detailed Gateway Cards Grid */}
                    {ncuvkzTestResult.gateways && ncuvkzTestResult.gateways.length > 0 && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-1">
                        {ncuvkzTestResult.gateways.map((gw) => (
                          <div 
                            key={gw.id} 
                            className={`p-3 rounded-lg border flex flex-col justify-between space-y-2 ${
                              gw.status === 'ready' 
                                ? 'bg-slate-800/80 border-emerald-600/60' 
                                : gw.status === 'missing_auth'
                                ? 'bg-slate-800/90 border-amber-600/60'
                                : 'bg-slate-800/90 border-rose-600/60'
                            }`}
                          >
                            <div>
                              <div className="flex items-center justify-between gap-2 mb-1.5">
                                <span className="font-bold text-white text-xs">{gw.name}</span>
                                <span className={`text-[10px] px-2 py-0.5 rounded font-bold shrink-0 ${
                                  gw.status === 'ready' 
                                    ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40' 
                                    : gw.status === 'missing_auth'
                                    ? 'bg-amber-500/20 text-amber-300 border border-amber-500/40'
                                    : 'bg-rose-500/20 text-rose-300 border border-rose-500/40'
                                }`}>
                                  {gw.icon} {gw.statusBadge}
                                </span>
                              </div>
                              <p className="text-[11px] text-slate-300 leading-relaxed">{gw.stateDescription}</p>
                            </div>
                            <div className="pt-2 border-t border-slate-700/60 text-[11px]">
                              <span className="text-amber-300 font-bold">👉 Що зробити оператору: </span>
                              <span className="text-slate-200">{gw.actionRequired}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Operator Next Steps Checklist */}
                    {ncuvkzTestResult.operatorChecklist && (
                      <div className="p-3 bg-slate-800/90 rounded-lg border border-slate-700 space-y-2 text-[11px]">
                        <div className="font-bold text-amber-300 uppercase tracking-wider flex items-center gap-1.5">
                          <span>📋 Пам'ятка оператора для запуску потоків даних:</span>
                        </div>
                        <div className="space-y-1 text-slate-200">
                          {ncuvkzTestResult.operatorChecklist.map((step, idx) => (
                            <div key={idx} className="flex items-start gap-1.5">
                              <span className="text-emerald-400">✔</span>
                              <span>{step}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Ping Feedback */}
                {ncuvkzPingResult.status !== 'idle' && (
                  <div className={`p-3 rounded text-xs font-mono border ${
                    ncuvkzPingResult.status === 'success' ? 'bg-blue-950/80 border-blue-500 text-blue-200' : 'bg-rose-950/80 border-rose-500 text-rose-200'
                  }`}>
                    {ncuvkzPingResult.message}
                  </div>
                )}

              </div>

            </div>
          )}
        </div>
      ) : activeSubTab === 'telegram' ? (
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-start gap-4 pb-6 border-b border-slate-200">
            <div className="p-3 bg-[#1E3A8A]/10 rounded border border-[#1E3A8A]/20 text-[#1E3A8A]">
              <Send className="w-6 h-6 animate-pulse" />
            </div>
            <div className="space-y-1">
              <h2 className="text-base font-bold font-mono uppercase tracking-wider text-slate-800">
                {language === 'uk' ? 'НАЛАШТУВАННЯ TELEGRAM БОТА' : 'TELEGRAM BOT SETTINGS'}
              </h2>
              <p className="text-[11px] text-slate-400 font-mono">
                {language === 'uk' 
                  ? 'КОНФІГУРАЦІЯ ОПОВІЩЕНЬ ПРО НОВИХ КОРИСТУВАЧІВ ТА ІНШІ СИСТЕМНІ ПОДІЇ' 
                  : 'CONFIGURE NOTIFICATIONS FOR NEW USERS AND OTHER SYSTEM EVENTS'}
              </p>
            </div>
          </div>

          <div className="space-y-6">
            {/* 1. Admin Alerts Configuration */}
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 space-y-4">
              <div className="flex items-center justify-between pb-3 border-b border-slate-200">
                <h3 className="text-xs font-bold font-mono text-slate-800 uppercase tracking-wider flex items-center gap-2">
                  <span>⚙️ АДМІНІСТРАТИВНІ СПОВІЩЕННЯ ТА CLI-ДИСПЕТЧЕР</span>
                </h3>
                <span className="text-[10px] font-mono text-slate-500 bg-white px-2 py-0.5 rounded border border-slate-200">
                  @Antigravity_cli_contabo_bot
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex flex-col gap-1.5">
                  <label className="text-[10px] font-bold text-slate-600 uppercase font-mono">
                    {language === 'uk' ? 'Токен Telegram Бота (Bot Token)' : 'Telegram Bot Token'}
                  </label>
                  <input
                    type="password"
                    value={telegramBotToken}
                    onChange={(e) => setTelegramBotToken(e.target.value)}
                    placeholder="8807025744:AAFfoQADHiUN5yKT7eDq00E5-FAmzM-B8eY"
                    className="w-full bg-white border border-slate-200 rounded px-3 py-2 text-xs text-slate-800 font-mono focus:outline-none focus:border-[#1E3A8A]"
                  />
                  <p className="text-[9px] text-slate-400 font-mono">
                    {language === 'uk' ? 'Токен бота для надсилання сповіщень та файлів.' : 'Bot token for sending alerts and reports.'}
                  </p>
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-[10px] font-bold text-slate-600 uppercase font-mono">
                    {language === 'uk' ? 'ID адміна (Chat ID)' : 'Admin Chat ID'}
                  </label>
                  <input
                    type="text"
                    value={telegramChatId}
                    onChange={(e) => setTelegramChatId(e.target.value)}
                    placeholder="1421638496"
                    className="w-full bg-white border border-slate-200 rounded px-3 py-2 text-xs text-slate-800 font-mono focus:outline-none focus:border-[#1E3A8A]"
                  />
                  <p className="text-[9px] text-slate-400 font-mono">
                    {language === 'uk' ? 'Приватний чат ID для внутрішніх звітів та радар-аналізу.' : 'Private chat ID for admin reports.'}
                  </p>
                </div>
              </div>
            </div>

            {/* 2. Public Telegram Channel for B2B Market Radar */}
            <div className="bg-gradient-to-br from-slate-900 via-indigo-950 to-blue-950 border border-blue-500/40 rounded-xl p-6 space-y-6 text-white shadow-xl">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-blue-800/60 pb-4">
                <div>
                  <div className="inline-flex items-center gap-2 px-3 py-1 bg-emerald-500/20 border border-emerald-400/40 rounded-full text-[11px] font-bold text-emerald-300 uppercase tracking-wider mb-2 font-mono">
                    📢 B2B Product-Led Growth (RICE 60.8)
                  </div>
                  <h3 className="text-base font-bold text-white uppercase font-mono tracking-tight flex items-center gap-2">
                    <Globe className="w-5 h-5 text-blue-400" />
                    <span>{language === 'uk' ? 'ПУБЛІЧНИЙ TELEGRAM-КАНАЛ РАДАРУ РЕГУЛЯТОРНИХ ЗМІН' : 'PUBLIC REGULATORY RADAR TELEGRAM CHANNEL'}</span>
                  </h3>
                  <p className="text-xs text-blue-200/80 font-mono mt-1 leading-relaxed">
                    {language === 'uk'
                      ? 'Автоматична щоденна публікація професійних оглядів законодавства (податки, ТЦУ, ZUS, митниця, бронювання) з прямими лінками на інструменти Deep Research Hub для конверсії B2B-клієнтів.'
                      : 'Automated daily broadcast of regulatory updates with direct links to calculators and generators for B2B client acquisition.'}
                  </p>
                </div>

                {lastMarketRadarPostedAt && (
                  <div className="text-right font-mono text-[10px] text-blue-300 bg-blue-900/50 px-3 py-1.5 rounded-lg border border-blue-700/50">
                    <div>Останній постінг:</div>
                    <div className="font-bold text-emerald-400">{new Date(lastMarketRadarPostedAt).toLocaleString('uk-UA')}</div>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-[11px] font-bold text-blue-200 uppercase font-mono flex items-center gap-1.5">
                    <span>📢 ID або @юзернейм публічного каналу:</span>
                  </label>
                  <input
                    type="text"
                    value={telegramPublicChannelId}
                    onChange={(e) => setTelegramPublicChannelId(e.target.value)}
                    placeholder="@RegulatoryRadarUA або -1001234567890"
                    className="w-full bg-slate-900/90 border border-blue-400/50 rounded-lg px-4 py-2.5 text-xs text-white font-mono focus:outline-none focus:border-blue-400 placeholder:text-slate-500"
                  />
                  <p className="text-[10px] text-blue-300/70 font-mono">
                    Вкажіть публічне посилання каналу (наприклад, <code className="text-blue-200">@RegulatoryRadarUA</code>) або цифровий Chat ID каналу.
                  </p>
                </div>

                <div className="flex flex-col justify-between p-3.5 bg-blue-950/60 border border-blue-800/60 rounded-lg">
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="autopostToggle"
                      checked={marketRadarPublicChannelAutopost}
                      onChange={(e) => setMarketRadarPublicChannelAutopost(e.target.checked)}
                      className="w-4 h-4 rounded text-blue-600 focus:ring-blue-500 border-slate-600 bg-slate-800"
                    />
                    <label htmlFor="autopostToggle" className="text-xs font-bold text-white font-mono cursor-pointer">
                      Автоматичний постінг кожні 24 години
                    </label>
                  </div>
                  <p className="text-[10px] text-blue-300/70 font-mono mt-1">
                    Після завершення щоденного радар-сканування новий B2B дайджест публікується у канал автоматично без ручних дій.
                  </p>
                </div>
              </div>

              {/* Status Alert */}
              {publicChannelStatusMsg && (
                <div className={`p-3 rounded-lg text-xs font-mono border flex items-center gap-2 ${
                  publicChannelStatusMsg.type === 'success'
                    ? 'bg-emerald-950/80 border-emerald-500 text-emerald-200'
                    : 'bg-rose-950/80 border-rose-500 text-rose-200'
                }`}>
                  {publicChannelStatusMsg.type === 'success' ? <CheckCircle2 className="w-4 h-4 shrink-0" /> : <AlertCircle className="w-4 h-4 shrink-0" />}
                  <span>{publicChannelStatusMsg.text}</span>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-wrap items-center gap-3 pt-1">
                <button
                  onClick={handleTestPublicChannel}
                  disabled={isTestingPublicChannel || !telegramPublicChannelId.trim()}
                  className="px-4 py-2 bg-indigo-900/70 hover:bg-indigo-800 border border-indigo-500/60 text-indigo-100 text-xs font-mono font-bold rounded-lg flex items-center gap-2 transition-all disabled:opacity-50 cursor-pointer"
                >
                  {isTestingPublicChannel ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                  <span>🧪 Перевірити канал (Тестовий пінг)</span>
                </button>

                <button
                  onClick={handlePublishPublicChannel}
                  disabled={isPublishingPublicChannel || !telegramPublicChannelId.trim()}
                  className="px-5 py-2 bg-emerald-600 hover:bg-emerald-500 border border-emerald-400 text-white text-xs font-mono font-bold rounded-lg flex items-center gap-2 transition-all shadow-lg hover:shadow-emerald-900/50 disabled:opacity-50 cursor-pointer"
                >
                  {isPublishingPublicChannel ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                  <span>📢 Опублікувати свіжий дайджест зараз</span>
                </button>

                <button
                  onClick={fetchPublicDigest}
                  disabled={isLoadingPublicDigest}
                  className="px-3.5 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-600 text-slate-300 text-xs font-mono rounded-lg flex items-center gap-1.5 transition-all cursor-pointer"
                  title="Оновити попередній перегляд"
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${isLoadingPublicDigest ? 'animate-spin' : ''}`} />
                  <span>Оновити прев'ю</span>
                </button>
              </div>

              {/* Quick 3-Step Instruction */}
              <div className="p-4 bg-slate-900/90 rounded-lg border border-blue-900/80 space-y-2 text-xs font-mono">
                <div className="font-bold text-amber-300 uppercase tracking-wider flex items-center gap-2">
                  <span>💡 Як підключити публічний канал за 3 прості кроки:</span>
                </div>
                <div className="space-y-1.5 text-blue-100/90 text-[11px]">
                  <div className="flex items-start gap-2">
                    <span className="text-emerald-400 font-bold">1.</span>
                    <span>Створіть публічний або приватний канал у Telegram (наприклад, <b>«Радар Регуляторних ЗМІН | Бізнес & Податки»</b>).</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-emerald-400 font-bold">2.</span>
                    <span>Додайте бота <code>@Antigravity_cli_contabo_bot</code> у свій канал як <b>Адміністратора</b> з увімкненим дозволом <i>«Публікація повідомлень»</i>.</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-emerald-400 font-bold">3.</span>
                    <span>Введіть <code>@юзернейм</code> каналу у поле вище, протестуйте кнопкою <i>«Перевірити канал»</i> та натисніть <i>«Зберегти всі налаштування»</i>.</span>
                  </div>
                </div>
              </div>

              {/* Live Preview of the Public Digest */}
              {cachedPublicDigest && (
                <div className="space-y-2 pt-2">
                  <div className="flex items-center justify-between text-xs font-mono text-blue-200">
                    <span className="font-bold uppercase tracking-wide flex items-center gap-1.5">
                      <span>👁️ Попередній перегляд публічного поста (Live Telegram Preview):</span>
                    </span>
                    <span className="text-[10px] text-blue-300/70">
                      Знахідок: {cachedPublicDigest.findingsCount || 0}
                    </span>
                  </div>
                  <div className="bg-slate-950/90 border border-blue-500/30 rounded-xl p-5 space-y-4 font-sans text-slate-100 shadow-inner max-h-96 overflow-y-auto">
                    <div 
                      className="text-xs leading-relaxed space-y-2 prose prose-invert max-w-none font-mono whitespace-pre-wrap"
                      dangerouslySetInnerHTML={{ __html: cachedPublicDigest.htmlText }}
                    />
                    
                    {/* Simulated Inline Buttons */}
                    {cachedPublicDigest.inlineKeyboard?.inline_keyboard && (
                      <div className="pt-3 border-t border-blue-900/60 space-y-1.5">
                        <div className="text-[10px] text-blue-400 font-mono uppercase tracking-wider">
                          Кнопки переходу для клієнтів (Inline Keyboard):
                        </div>
                        <div className="flex flex-col gap-1.5">
                          {cachedPublicDigest.inlineKeyboard.inline_keyboard.map((row: any[], rIdx: number) => (
                            <div key={rIdx} className="flex gap-2">
                              {row.map((btn: any, bIdx: number) => (
                                <a
                                  key={bIdx}
                                  href={btn.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex-1 py-2 px-3 bg-blue-900/70 hover:bg-blue-800 border border-blue-500/60 rounded text-center text-xs font-bold font-mono text-white flex items-center justify-center gap-1.5 transition-all"
                                >
                                  <span>{btn.text}</span>
                                  <ExternalLink className="w-3 h-3 text-blue-300" />
                                </a>
                              ))}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {isLoadingTelegram ? (
              <div className="flex flex-col items-center py-6 text-slate-400 font-mono text-xs gap-3">
                <RefreshCw className="w-6 h-6 animate-spin text-[#1E3A8A]" />
                <span>{language === 'uk' ? 'ЗАВАНТАЖЕННЯ НАЛАШТУВАНЬ TELEGRAM...' : 'LOADING TELEGRAM SETTINGS...'}</span>
              </div>
            ) : null}

            <div className="pt-4 flex justify-end">
              <button
                onClick={handleSaveTelegramSettings}
                disabled={isSavingTelegram || isLoadingTelegram}
                className="px-6 py-2.5 bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 disabled:bg-slate-100 disabled:text-slate-400 text-white font-bold font-mono text-[11px] uppercase rounded transition-all cursor-pointer flex items-center gap-2"
              >
                {isSavingTelegram ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : null}
                <span>{language === 'uk' ? 'Зберегти всі налаштування Telegram' : 'Save All Telegram Settings'}</span>
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {activeSubTab === 'email' ? (
        <div className="space-y-6">
          <div className="flex items-start gap-4 pb-6 border-b border-slate-200">
            <div className="p-3 bg-blue-50 rounded border border-blue-200 text-[#1E3A8A]">
              <Mail className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-base font-bold text-slate-900 uppercase font-mono">
                {language === 'uk' ? 'EMAIL РОЗСИЛКИ ТА НАГАДУВАННЯ' : 'EMAIL MAILING & REMINDERS'}
              </h2>
              <p className="text-xs text-slate-500 font-mono mt-1">
                {language === 'uk'
                  ? 'Керування автоматичними нагадуваннями неактивним користувачам, налаштування SMTP та перегляд логів надсилання.'
                  : 'Manage automated inactivity reminders to users, SMTP server settings, and view email delivery logs.'}
              </p>
            </div>
          </div>

          {/* SMART RATE-LIMITED EMAIL DISPATCH QUEUE CARD */}
          <div className="bg-gradient-to-br from-slate-900 via-indigo-950 to-blue-950 border border-blue-500/40 rounded-xl p-6 space-y-5 shadow-xl text-white">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-blue-800/60 pb-4">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-blue-500/20 border border-blue-400/40 rounded-full text-[11px] font-bold text-blue-300 uppercase tracking-wider mb-2 font-mono">
                  ⚡ Розумна Черга з Авто-Лімітами (Gmail SMTP Safe)
                </div>
                <h3 className="text-base font-bold text-white uppercase font-mono tracking-tight flex items-center gap-2">
                  <Mail className="w-5 h-5 text-blue-400" />
                  <span>{language === 'uk' ? 'АВТОМАТИЧНА ЧЕРГА РОЗСИЛКИ В МЕЖАХ ДОБОВОГО ЛІМІТУ' : 'DAILY RATE-LIMITED DISPATCH QUEUE'}</span>
                </h3>
                <p className="text-xs text-blue-200/80 font-mono mt-1 leading-relaxed">
                  {language === 'uk'
                    ? 'Система відправляє листи порціями до 450 листів/добу. Все, що перевищує ліміт Gmail, автоматично зберігається в базі та продовжує відправлятися наступного дня о 08:00.'
                    : 'System dispatches emails safely up to 450 emails/day. Anything beyond the Gmail quota is stored and resumed automatically next morning at 08:00 AM.'}
                </p>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={fetchEmailQueueStats}
                  className="px-3 py-1.5 bg-blue-900/60 hover:bg-blue-800 border border-blue-700/60 text-blue-200 text-xs font-mono rounded flex items-center gap-1.5 transition-all cursor-pointer"
                  title="Оновити статистику черги"
                >
                  <RefreshCw className="w-3.5 h-3.5" />
                  <span>Оновити</span>
                </button>
              </div>
            </div>

            {/* Live Queue Indicators Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="bg-slate-900/80 border border-blue-900/50 rounded-lg p-3 text-center">
                <div className="text-[10px] font-bold text-blue-300 font-mono uppercase">Всього в черзі</div>
                <div className="text-xl font-extrabold text-white font-mono mt-0.5">
                  {emailQueueStats?.totalInQueue ?? '—'}
                </div>
              </div>

              <div className="bg-slate-900/80 border border-amber-900/50 rounded-lg p-3 text-center">
                <div className="text-[10px] font-bold text-amber-300 font-mono uppercase">Очікує відправки</div>
                <div className="text-xl font-extrabold text-amber-400 font-mono mt-0.5">
                  {emailQueueStats?.pendingCount ?? '—'}
                </div>
              </div>

              <div className="bg-slate-900/80 border border-emerald-900/50 rounded-lg p-3 text-center">
                <div className="text-[10px] font-bold text-emerald-300 font-mono uppercase">Надіслано сьогодні</div>
                <div className="text-xl font-extrabold text-emerald-400 font-mono mt-0.5">
                  {emailQueueStats?.sentToday ?? 0} / {emailQueueStats?.dailyLimit ?? 450}
                </div>
              </div>

              <div className="bg-slate-900/80 border border-purple-900/50 rounded-lg p-3 text-center">
                <div className="text-[10px] font-bold text-purple-300 font-mono uppercase">Надіслано всього</div>
                <div className="text-xl font-extrabold text-purple-300 font-mono mt-0.5">
                  {emailQueueStats?.sentTotal ?? '—'}
                </div>
              </div>
            </div>

            {/* Quota Status Banner */}
            {emailQueueStats?.isQuotaLocked ? (
              <div className="p-3.5 bg-amber-950/60 border border-amber-500/50 rounded-lg flex items-start gap-3 text-amber-200 text-xs font-mono">
                <AlertCircle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
                <div>
                  <div className="font-bold text-amber-300">⏸️ Добовий ліміт Gmail вичерпано на сьогодні</div>
                  <div className="text-[11px] text-amber-200/90 mt-0.5">
                    Чергу автоматично збережено. Наступна порція відправки розпочнеться автоматично завтра о 08:00 (після скидання ліміту Google).
                  </div>
                </div>
              </div>
            ) : (
              <div className="p-3 bg-emerald-950/40 border border-emerald-500/40 rounded-lg flex items-center gap-2 text-emerald-300 text-xs font-mono">
                <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                <span>
                  🟢 Шлюз активний: доступно для відправки сьогодні ~{Math.max(0, (emailQueueStats?.dailyLimit ?? 450) - (emailQueueStats?.sentToday ?? 0))} листів.
                </span>
              </div>
            )}

            {/* Regional Priority Breakdown */}
            <div className="p-3 bg-slate-950/60 border border-blue-900/50 rounded-lg flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 text-xs font-mono">
              <div className="flex items-center gap-2 text-blue-300 font-bold">
                <span className="w-2 h-2 rounded-full bg-blue-400 animate-pulse"></span>
                <span>🎯 Черговість відправки (Пріоритет):</span>
              </div>
              <div className="flex flex-wrap items-center gap-2 text-[11px]">
                <span className="px-2.5 py-1 bg-blue-900/40 border border-blue-400/50 text-blue-200 rounded font-bold">
                  1. 🇺🇦 Україна: {emailQueueStats?.pendingUkraine ?? 0}
                </span>
                <span className="text-slate-500 font-bold">→</span>
                <span className="px-2.5 py-1 bg-rose-950/40 border border-rose-400/50 text-rose-200 rounded font-bold">
                  2. 🇵🇱 Польща: {emailQueueStats?.pendingPoland ?? 0}
                </span>
                <span className="text-slate-500 font-bold">→</span>
                <span className="px-2.5 py-1 bg-indigo-950/40 border border-indigo-400/50 text-indigo-200 rounded font-bold">
                  3. 🇪🇺 Євросоюз: {emailQueueStats?.pendingEu ?? 0}
                </span>
              </div>
            </div>

            {queueActionMsg && (
              <div className="p-3 bg-blue-950/60 border border-blue-500/50 rounded-lg text-xs text-blue-200 font-mono">
                {queueActionMsg}
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-3 pt-2">
              <button
                onClick={handleEnqueueAllLeads}
                disabled={isEnqueuingQueue || isProcessingQueue}
                className="px-5 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 disabled:opacity-50 text-white font-bold font-mono text-xs uppercase rounded-lg transition-all cursor-pointer flex items-center gap-2 shadow-lg shadow-blue-900/40"
              >
                {isEnqueuingQueue ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                <span>📥 Додати всі контакти (1900+) у чергу авто-відправки</span>
              </button>

              <button
                onClick={() => handleProcessQueueNow(false)}
                disabled={isProcessingQueue || isEnqueuingQueue || (emailQueueStats?.pendingCount === 0)}
                className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white font-bold font-mono text-xs uppercase rounded-lg transition-all cursor-pointer flex items-center gap-2 shadow-lg shadow-emerald-900/40"
              >
                {isProcessingQueue ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                <span>🚀 Відправити зараз у межах ліміту ({Math.min(emailQueueStats?.pendingCount || 0, Math.max(0, (emailQueueStats?.dailyLimit || 450) - (emailQueueStats?.sentToday || 0)))})</span>
              </button>

              {(emailQueueStats?.failedCount || 0) > 0 && (
                <button
                  onClick={handleClearFailedQueue}
                  className="px-4 py-2.5 bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700 font-mono text-xs rounded-lg transition-all cursor-pointer flex items-center gap-1.5"
                >
                  <RefreshCw className="w-3.5 h-3.5" />
                  <span>Повторити помилкові ({emailQueueStats?.failedCount})</span>
                </button>
              )}
            </div>
          </div>


          {/* Send Invitations to All Users Card */}
          <div className="bg-gradient-to-r from-blue-900/5 via-indigo-900/5 to-slate-50 border border-blue-200 rounded-lg p-5 space-y-4 shadow-sm">
            <h3 className="text-xs font-bold text-slate-800 uppercase font-mono flex items-center gap-2">
              <Mail className="w-4 h-4 text-[#1E3A8A]" />
              <span>{language === 'uk' ? 'Розсилка запрошень усім користувачам' : 'Send Invitations to All Users'}</span>
            </h3>
            
            <p className="text-xs text-slate-600 font-mono leading-relaxed">
              {language === 'uk'
                ? 'Надіслати електронне запрошення з оглядом можливостей платформи Personal AI Assistant & Deep Research Hub усім підтвердженим користувачам.'
                : 'Send an email invitation showcasing Personal AI Assistant & Deep Research Hub features to all approved users.'}
            </p>

            <div className="space-y-1.5">
              <label className="text-[10px] font-bold text-slate-500 uppercase font-mono">
                {language === 'uk' ? 'Персональне повідомлення/примітка (опціонально)' : 'Custom Note/Message (Optional)'}
              </label>
              <textarea
                value={invitationCustomNote}
                onChange={(e) => setInvitationCustomNote(e.target.value)}
                placeholder={language === 'uk' ? 'Наприклад: Запрошуємо випробувати нові агенти спецконтролю, супутникової аналітики та ДСТУ діловодства!' : 'E.g. We invite you to try out new space analytics, special control and legal agents!'}
                className="w-full bg-white border border-slate-200 rounded px-3 py-2 text-xs font-mono focus:outline-none focus:border-[#1E3A8A] h-20 resize-none"
              />
            </div>

            {invitationAllResult && (
              <div className="p-3 bg-emerald-50 border border-emerald-200 rounded text-xs text-emerald-800 font-mono">
                {invitationAllResult}
              </div>
            )}

            <div className="flex flex-wrap gap-3 pt-1">
              <button
                onClick={handleSendInvitationAll}
                disabled={isSendingInvitationAll}
                className="px-5 py-2.5 bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 disabled:opacity-50 text-white font-bold font-mono text-xs uppercase rounded transition-all cursor-pointer flex items-center gap-2 shadow-sm"
              >
                {isSendingInvitationAll ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                <span>{language === 'uk' ? 'Надіслати запрошення всім користувачам' : 'Send Invitation to All Users'}</span>
              </button>
            </div>
          </div>

          {/* Direct Actions & Triggers */}
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-5 space-y-4">
            <h3 className="text-xs font-bold text-slate-800 uppercase font-mono flex items-center gap-2">
              <Send className="w-4 h-4 text-[#1E3A8A]" />
              <span>{language === 'uk' ? 'Запуск розсилки-нагадування' : 'Trigger Reminder Broadcast'}</span>
            </h3>
            
            <p className="text-xs text-slate-600 font-mono leading-relaxed">
              {language === 'uk'
                ? 'Надіслати електронні листи-нагадування користувачам, які давно не заходили на платформу (або виконати примусову першу розсилку всім підтвердженим користувачам).'
                : 'Send email reminders to users who have been inactive for a while (or force launch first broadcast to all approved users).'}
            </p>

            {inactivityReminderResult && (
              <div className="p-3 bg-emerald-50 border border-emerald-200 rounded text-xs text-emerald-800 font-mono">
                {inactivityReminderResult}
              </div>
            )}

            <div className="flex flex-wrap gap-3 pt-2">
              <button
                onClick={() => handleTriggerInactivityReminders(true)}
                disabled={isSendingInactivityReminder}
                className="px-4 py-2 bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 disabled:opacity-50 text-white font-bold font-mono text-xs rounded transition-all cursor-pointer flex items-center gap-2"
              >
                {isSendingInactivityReminder ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                <span>{language === 'uk' ? 'Запустити першу розсилку (всім)' : 'Launch First Broadcast (All)'}</span>
              </button>

              <button
                onClick={() => handleTriggerInactivityReminders(false)}
                disabled={isSendingInactivityReminder}
                className="px-4 py-2 bg-slate-200 hover:bg-slate-300 disabled:opacity-50 text-slate-800 font-bold font-mono text-xs rounded transition-all cursor-pointer flex items-center gap-2"
              >
                {isSendingInactivityReminder ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                <span>{language === 'uk' ? 'Перевірити неактивних (14+ днів)' : 'Check Inactive (14+ days)'}</span>
              </button>
            </div>
          </div>

          {/* SMTP Configuration Form */}
          <div className="space-y-4 pt-2">
            <h3 className="text-xs font-bold text-slate-800 uppercase font-mono">
              {language === 'uk' ? 'Налаштування SMTP сервера' : 'SMTP Server Configuration'}
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex flex-col gap-1.5">
                <label className="text-[10px] font-bold text-slate-500 uppercase font-mono">
                  SMTP Host
                </label>
                <input
                  type="text"
                  value={smtpSettings.host}
                  onChange={(e) => setSmtpSettings({ ...smtpSettings, host: e.target.value })}
                  placeholder="smtp.gmail.com"
                  className="w-full bg-white border border-slate-200 rounded px-3 py-2 text-xs font-mono focus:outline-none focus:border-[#1E3A8A]"
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-[10px] font-bold text-slate-500 uppercase font-mono">
                  SMTP Port
                </label>
                <input
                  type="text"
                  value={smtpSettings.port}
                  onChange={(e) => setSmtpSettings({ ...smtpSettings, port: e.target.value })}
                  placeholder="587"
                  className="w-full bg-white border border-slate-200 rounded px-3 py-2 text-xs font-mono focus:outline-none focus:border-[#1E3A8A]"
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-[10px] font-bold text-slate-500 uppercase font-mono">
                  SMTP User
                </label>
                <input
                  type="text"
                  value={smtpSettings.user}
                  onChange={(e) => setSmtpSettings({ ...smtpSettings, user: e.target.value })}
                  placeholder="your-email@domain.com"
                  className="w-full bg-white border border-slate-200 rounded px-3 py-2 text-xs font-mono focus:outline-none focus:border-[#1E3A8A]"
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-[10px] font-bold text-slate-500 uppercase font-mono">
                  SMTP Password
                </label>
                <input
                  type="password"
                  value={smtpSettings.pass}
                  onChange={(e) => setSmtpSettings({ ...smtpSettings, pass: e.target.value })}
                  placeholder="••••••••••••"
                  className="w-full bg-white border border-slate-200 rounded px-3 py-2 text-xs font-mono focus:outline-none focus:border-[#1E3A8A]"
                />
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-[10px] font-bold text-slate-500 uppercase font-mono">
                {language === 'uk' ? 'Адреса відправника (From Email)' : 'From Email Address'}
              </label>
              <input
                type="text"
                value={smtpSettings.from}
                onChange={(e) => setSmtpSettings({ ...smtpSettings, from: e.target.value })}
                placeholder="Deep Research Hub <noreply@ai-assistent.online>"
                className="w-full bg-white border border-slate-200 rounded px-3 py-2 text-xs font-mono focus:outline-none focus:border-[#1E3A8A]"
              />
              <p className="text-[9px] text-slate-400 font-mono">
                {language === 'uk'
                  ? 'Якщо параметри SMTP порожні, листи під час тестування автоматично записуються в системний лог-файл (sent_emails.log).'
                  : 'If SMTP parameters are empty, emails during testing are logged to sent_emails.log file.'}
              </p>
            </div>

            {smtpTestResult && (
              <div className={`p-3 rounded-lg text-xs font-mono border ${
                smtpTestResult.success 
                  ? 'bg-emerald-50 border-emerald-300 text-emerald-800' 
                  : 'bg-rose-50 border-rose-300 text-rose-800'
              }`}>
                {smtpTestResult.message}
              </div>
            )}

            <div className="pt-2 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={handleTestSmtpConnection}
                disabled={isTestingSmtp || isSavingSmtp}
                className="px-4 py-2 bg-slate-700 hover:bg-slate-800 disabled:opacity-50 text-white font-bold font-mono text-xs uppercase rounded transition-all cursor-pointer flex items-center gap-1.5"
              >
                {isTestingSmtp ? <RefreshCw className="w-3.5 h-3.5 animate-spin text-amber-400" /> : <Send className="w-3.5 h-3.5 text-amber-400" />}
                <span>{language === 'uk' ? '🧪 Тестувати SMTP' : '🧪 Test SMTP'}</span>
              </button>

              <button
                type="button"
                onClick={handleSaveSmtpSettings}
                disabled={isSavingSmtp || isTestingSmtp}
                className="px-5 py-2 bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 disabled:opacity-50 text-white font-bold font-mono text-xs uppercase rounded transition-all cursor-pointer flex items-center gap-2"
              >
                {isSavingSmtp ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : null}
                <span>{language === 'uk' ? 'Зберегти SMTP' : 'Save SMTP'}</span>
              </button>
            </div>
          </div>

          {/* Email Logs Monitor */}
          <div className="space-y-3 pt-4 border-t border-slate-200">
            <div className="flex items-center justify-between">
              <h3 className="text-xs font-bold text-slate-800 uppercase font-mono">
                {language === 'uk' ? 'Останні логи надсилання' : 'Recent Email Logs'}
              </h3>
              <button
                onClick={fetchEmailSettingsAndLogs}
                className="p-1 text-slate-500 hover:text-slate-800 transition-colors"
                title="Оновити логи"
              >
                <RefreshCw className="w-3.5 h-3.5" />
              </button>
            </div>

            <div className="bg-slate-900 text-emerald-400 p-4 rounded-lg font-mono text-[11px] h-48 overflow-y-auto whitespace-pre-wrap leading-relaxed border border-slate-800 shadow-inner">
              {emailLogs || (language === 'uk' ? 'Логи відсутні' : 'No logs available')}
            </div>
          </div>
        </div>
      ) : null}

      {/* Email Auto-Monitors Subtab Panel */}
      {activeSubTab === 'emailMonitors' ? (
        <div className="space-y-6">
          <div className="flex items-start gap-4 pb-6 border-b border-slate-200">
            <div className="p-3 bg-emerald-50 rounded border border-emerald-200 text-emerald-700">
              <Mail className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-base font-bold text-slate-900 uppercase font-mono flex items-center gap-2">
                <span>{language === 'uk' ? '📮 АВТО-МОНІТОРИНГ ВХІДНОЇ ПОШТИ (UKR.NET & GMAIL)' : '📮 INCOMING EMAIL AUTO-MONITORING'}</span>
                <span className="text-[10px] px-2 py-0.5 bg-emerald-100 text-emerald-800 rounded font-bold">24/7 AUTO-LEAD</span>
              </h2>
              <p className="text-xs text-slate-500 font-mono mt-1">
                {language === 'uk'
                  ? 'Автоматичне сканування вхідних листів на ваші поштові скриньки Ukr.net та Gmail 24/7. Користувачі з України автоматично додаються в базу з доступом і отримують запрошення зі списком усіх ШІ-агентів.'
                  : 'Automated 24/7 inbox monitoring for Ukr.net & Gmail. Senders from Ukraine are registered in DB and receive instant invitations with full agent catalog.'}
              </p>
            </div>
          </div>

          {/* Actions and Status Bar */}
          <div className="flex flex-wrap items-center justify-between gap-3 bg-slate-50 border border-slate-200 p-4 rounded-lg">
            <div className="flex items-center gap-2">
              <button
                onClick={async () => {
                  setIsScanningMonitors(true);
                  setMonitorMessage(null);
                  try {
                    const res = await fetch('/api/admin/email-monitors/trigger', { method: 'POST' });
                    const data = await res.json();
                    if (res.ok) {
                      setMonitorMessage(data.message);
                      fetchEmailMonitors();
                    } else {
                      setMonitorMessage(`Помилка: ${data.error}`);
                    }
                  } catch (err: any) {
                    setMonitorMessage(`Помилка запуску: ${err.message}`);
                  } finally {
                    setIsScanningMonitors(false);
                  }
                }}
                disabled={isScanningMonitors}
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 text-white font-mono text-xs font-bold rounded flex items-center gap-2 transition-all cursor-pointer shadow-xs"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${isScanningMonitors ? 'animate-spin' : ''}`} />
                <span>{isScanningMonitors ? (language === 'uk' ? 'Сканування...' : 'Scanning...') : (language === 'uk' ? '⚡ Запустити сканування зараз' : '⚡ Trigger Scan Now')}</span>
              </button>
              <button
                onClick={fetchEmailMonitors}
                disabled={isLoadingMonitors}
                className="px-3 py-2 bg-white border border-slate-300 hover:bg-slate-100 text-slate-700 font-mono text-xs font-bold rounded flex items-center gap-1.5 transition-all cursor-pointer"
              >
                <RefreshCw className="w-3.5 h-3.5 text-slate-500" />
                <span>{language === 'uk' ? 'Оновити статус' : 'Refresh'}</span>
              </button>
            </div>

            <div className="text-xs font-mono text-slate-600">
              <span>Підключено скриньок: <strong>{emailMonitors.length}</strong></span>
            </div>
          </div>

          {monitorMessage && (
            <div className="p-3 bg-emerald-50 border border-emerald-200 rounded text-xs font-mono text-emerald-800 flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-emerald-600 shrink-0" />
              <span>{monitorMessage}</span>
            </div>
          )}

          {/* Connected Accounts Cards */}
          <div className="space-y-3">
            <h3 className="text-xs font-bold text-slate-700 uppercase font-mono flex items-center gap-2">
              <Users className="w-4 h-4 text-[#1E3A8A]" />
              <span>{language === 'uk' ? 'Підключені поштові скриньки для авто-моніторингу' : 'Connected Email Accounts'}</span>
            </h3>

            {emailMonitors.length === 0 ? (
              <div className="p-6 bg-slate-50 border border-slate-200 rounded text-center text-xs font-mono text-slate-500">
                Немає підключених поштових скриньок. Додайте пошту Ukr.net або Gmail нижче.
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-3">
                {emailMonitors.map((acc: any) => (
                  <div key={acc.id} className="bg-white border border-slate-200 rounded-lg p-4 flex flex-col md:flex-row md:items-center justify-between gap-4 shadow-2xs">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2.5">
                        <span className="font-mono text-sm font-bold text-slate-900">{acc.email}</span>
                        <span className={`text-[10px] font-bold font-mono px-2 py-0.5 rounded ${
                          acc.provider === 'ukr.net' ? 'bg-amber-100 text-amber-800 border border-amber-300' : 'bg-blue-100 text-blue-800 border border-blue-300'
                        }`}>
                          {acc.provider.toUpperCase()}
                        </span>
                        <span className={`text-[10px] font-bold font-mono px-2 py-0.5 rounded ${
                          acc.isActive ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-500'
                        }`}>
                          {acc.isActive ? '🟢 АКТИВНИЙ 24/7' : '⚪ ВИМКНЕНО'}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 font-mono">{acc.description}</p>
                      <div className="flex flex-wrap gap-4 text-[11px] font-mono text-slate-600 pt-1">
                        <span>Останнє сканування: <strong>{acc.lastRunAt ? new Date(acc.lastRunAt).toLocaleString('uk-UA') : 'Не проводилось'}</strong></span>
                        <span>Залучено лідів з України: <strong className="text-emerald-700">👤 {acc.totalLeadsCount || 0}</strong></span>
                      </div>
                      {acc.lastError && (
                        <p className="text-[11px] font-mono text-red-600 bg-red-50 p-1.5 rounded border border-red-200 mt-1">
                          ⚠️ {acc.lastError}
                        </p>
                      )}
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        onClick={async () => {
                          try {
                            const res = await fetch('/api/admin/email-monitors/toggle', {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({ email: acc.email, isActive: !acc.isActive })
                            });
                            if (res.ok) fetchEmailMonitors();
                          } catch (err) {
                            console.error(err);
                          }
                        }}
                        className={`px-3 py-1.5 font-mono text-xs font-bold rounded transition-all cursor-pointer ${
                          acc.isActive 
                            ? 'bg-amber-50 hover:bg-amber-100 text-amber-800 border border-amber-200' 
                            : 'bg-emerald-50 hover:bg-emerald-100 text-emerald-800 border border-emerald-200'
                        }`}
                      >
                        {acc.isActive ? 'Вимкнути' : 'Увімкнути'}
                      </button>
                      <button
                        onClick={async () => {
                          if (!confirm(`Вилучити пошту ${acc.email} з моніторингу?`)) return;
                          try {
                            const res = await fetch('/api/admin/email-monitors/delete', {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({ email: acc.email })
                            });
                            if (res.ok) fetchEmailMonitors();
                          } catch (err) {
                            console.error(err);
                          }
                        }}
                        className="p-1.5 text-slate-400 hover:text-red-600 transition-colors cursor-pointer"
                        title="Видалити"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Form: Add New Account */}
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-5 space-y-4 shadow-sm">
            <h3 className="text-xs font-bold text-slate-900 uppercase font-mono flex items-center gap-2">
              <UserPlus className="w-4 h-4 text-emerald-600" />
              <span>{language === 'uk' ? '➕ Підключити нову пошту Ukr.net або Gmail' : '➕ Connect New Ukr.net / Gmail Account'}</span>
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-[10px] font-bold text-slate-600 uppercase font-mono">
                  {language === 'uk' ? 'Email адреса' : 'Email Address'}
                </label>
                <input
                  type="email"
                  value={newMonitorEmail}
                  onChange={(e) => setNewMonitorEmail(e.target.value)}
                  placeholder="наприклад: bass776@ukr.net"
                  className="w-full px-3 py-2 bg-white border border-slate-300 rounded text-xs font-mono text-slate-900 focus:outline-none focus:border-emerald-500"
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-[10px] font-bold text-slate-600 uppercase font-mono">
                  {language === 'uk' ? 'Пароль додатку (IMAP Password)' : 'App Password (IMAP)'}
                </label>
                <input
                  type="password"
                  value={newMonitorPassword}
                  onChange={(e) => setNewMonitorPassword(e.target.value)}
                  placeholder="наприклад: 9gB4B7fMrKCFsYXU"
                  className="w-full px-3 py-2 bg-white border border-slate-300 rounded text-xs font-mono text-slate-900 focus:outline-none focus:border-emerald-500"
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-[10px] font-bold text-slate-600 uppercase font-mono">
                  {language === 'uk' ? 'Провайдер' : 'Provider'}
                </label>
                <select
                  value={newMonitorProvider}
                  onChange={(e: any) => setNewMonitorProvider(e.target.value)}
                  className="w-full px-3 py-2 bg-white border border-slate-300 rounded text-xs font-mono text-slate-900 focus:outline-none focus:border-emerald-500 font-bold"
                >
                  <option value="ukr.net">Ukr.net (imap.ukr.net:993)</option>
                  <option value="gmail">Gmail (imap.gmail.com:993)</option>
                </select>
              </div>

              <div className="space-y-1.5">
                <label className="text-[10px] font-bold text-slate-600 uppercase font-mono">
                  {language === 'uk' ? 'Опис / Назва пошти (опціонально)' : 'Description (Optional)'}
                </label>
                <input
                  type="text"
                  value={newMonitorDescription}
                  onChange={(e) => setNewMonitorDescription(e.target.value)}
                  placeholder="наприклад: Пошта відділу закупівель"
                  className="w-full px-3 py-2 bg-white border border-slate-300 rounded text-xs font-mono text-slate-900 focus:outline-none focus:border-emerald-500"
                />
              </div>
            </div>

            <button
              onClick={async () => {
                if (!newMonitorEmail || !newMonitorPassword) {
                  alert('Вкажіть email та пароль додатку IMAP.');
                  return;
                }
                setIsAddingMonitor(true);
                try {
                  const res = await fetch('/api/admin/email-monitors/add', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                      email: newMonitorEmail,
                      password: newMonitorPassword,
                      provider: newMonitorProvider,
                      description: newMonitorDescription
                    })
                  });
                  const data = await res.json();
                  if (res.ok) {
                    setNewMonitorEmail('');
                    setNewMonitorPassword('');
                    setNewMonitorDescription('');
                    fetchEmailMonitors();
                    alert(data.message);
                  } else {
                    alert(`Помилка: ${data.error}`);
                  }
                } catch (err: any) {
                  alert(`Помилка з'єднання: ${err.message}`);
                } finally {
                  setIsAddingMonitor(false);
                }
              }}
              disabled={isAddingMonitor || !newMonitorEmail}
              className="w-full py-2.5 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 text-white font-mono text-xs font-bold rounded uppercase tracking-wider flex items-center justify-center gap-2 transition-all cursor-pointer shadow-xs"
            >
              <UserPlus className="w-4 h-4" />
              <span>{isAddingMonitor ? (language === 'uk' ? 'Підключення...' : 'Connecting...') : (language === 'uk' ? '🚀 Підключити пошту до авто-моніторингу' : '🚀 Connect Email Account')}</span>
            </button>

            {/* Guide Note */}
            <div className="bg-amber-50 border border-amber-200 rounded p-3 text-[11px] font-mono text-amber-900 space-y-1">
              <p className="font-bold uppercase">💡 Інструкція з отримання Пароля додатка IMAP:</p>
              <p>• <strong>Ukr.net:</strong> Зайдіть у пошту ukr.net -&gt; *Налаштування* -&gt; *Керування акаунтом* -&gt; *Паролі для зовнішніх програм* -&gt; Створіть новий пароль (наприклад: <code className="bg-amber-100 px-1 py-0.5 rounded font-bold">9gB4B7fMrKCFsYXU</code>).</p>
              <p>• <strong>Gmail:</strong> Зайдіть у акаунт Google (myaccount.google.com) -&gt; *Безпека* -&gt; *Двоетапна перевірка* -&gt; *Паролі застосунків* -&gt; Створіть 16-значний пароль.</p>
            </div>
          </div>
        </div>
      ) : null}

      {/* Agent Usage Statistics Subtab Panel */}
      {activeSubTab === 'agentStats' ? (
        <div className="space-y-6">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-6 border-b border-slate-200">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-blue-50 rounded border border-blue-200 text-[#1E3A8A]">
                <BarChart3 className="w-6 h-6" />
              </div>
              <div className="space-y-1">
                <h2 className="text-base font-bold font-mono uppercase tracking-wider text-slate-800">
                  {language === 'uk' ? 'СТАТИСТИКА ВИКОРИСТАННЯ АГЕНТІВ' : 'AGENT USAGE STATISTICS'}
                </h2>
                <p className="text-[11px] text-slate-400 font-mono">
                  {language === 'uk'
                    ? 'ОБЛІК ЗАПУСКІВ АГЕНТІВ КОРИСТУВАЧАМИ (ВСЬОГО ТА ЗА ОСТАННІ 30 ДНІВ) • СИНХРОНІЗАЦІЯ З AGENT_USAGE_STATS.MD'
                    : 'TRACK AGENT USAGE BY USERS (TOTAL & LAST 30 DAYS) • SYNCED WITH AGENT_USAGE_STATS.MD'}
                </p>
              </div>
            </div>

            <button
              onClick={handleRefreshAgentStats}
              disabled={isLoadingUsageStats}
              className="px-4 py-2 bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 text-white rounded font-mono text-xs font-bold uppercase transition-all cursor-pointer flex items-center justify-center gap-2 disabled:opacity-50 self-start sm:self-auto shadow-sm"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${isLoadingUsageStats ? 'animate-spin' : ''}`} />
              <span>{language === 'uk' ? 'Оновити .md файл' : 'Refresh .md File'}</span>
            </button>
          </div>

          {/* Key Metrics Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl space-y-1">
              <div className="flex items-center justify-between text-slate-400 text-xs font-mono">
                <span>{language === 'uk' ? 'Всього запусків' : 'Total Runs'}</span>
                <Activity className="w-4 h-4 text-blue-600" />
              </div>
              <p className="text-2xl font-bold font-mono text-slate-800">
                {usageStatsData?.totals?.total_runs || 0}
              </p>
            </div>

            <div className="p-4 bg-emerald-50/60 border border-emerald-200 rounded-xl space-y-1">
              <div className="flex items-center justify-between text-emerald-600 text-xs font-mono">
                <span>{language === 'uk' ? 'За 30 днів' : 'Last 30 Days'}</span>
                <TrendingUp className="w-4 h-4 text-emerald-600" />
              </div>
              <p className="text-2xl font-bold font-mono text-emerald-900">
                {usageStatsData?.totals?.total_runs_30d || 0}
              </p>
            </div>

            <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl space-y-1">
              <div className="flex items-center justify-between text-slate-400 text-xs font-mono">
                <span>{language === 'uk' ? 'Користувачів' : 'Active Users'}</span>
                <Users className="w-4 h-4 text-indigo-600" />
              </div>
              <p className="text-2xl font-bold font-mono text-slate-800">
                {usageStatsData?.totals?.total_users || 0}
              </p>
            </div>

            <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl space-y-1">
              <div className="flex items-center justify-between text-slate-400 text-xs font-mono">
                <span>{language === 'uk' ? 'Агентів' : 'Active Agents'}</span>
                <Sparkles className="w-4 h-4 text-amber-500" />
              </div>
              <p className="text-2xl font-bold font-mono text-slate-800">
                {usageStatsData?.totals?.total_agents || 0}
              </p>
            </div>
          </div>

          {/* Controls & Search */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 pt-2">
            {/* View Mode Buttons */}
            <div className="flex flex-wrap items-center bg-slate-100 p-1 rounded-lg font-mono text-[11px] font-bold gap-1">
              <button
                onClick={() => setStatsViewMode('userAgent')}
                className={`px-3 py-1.5 rounded transition-all cursor-pointer ${
                  statsViewMode === 'userAgent'
                    ? 'bg-white text-[#1E3A8A] shadow-xs'
                    : 'text-slate-500 hover:text-slate-800'
                }`}
              >
                {language === 'uk' ? 'Детально (User × Agent)' : 'User × Agent'}
              </button>
              <button
                onClick={() => setStatsViewMode('userSummary')}
                className={`px-3 py-1.5 rounded transition-all cursor-pointer ${
                  statsViewMode === 'userSummary'
                    ? 'bg-white text-[#1E3A8A] shadow-xs'
                    : 'text-slate-500 hover:text-slate-800'
                }`}
              >
                {language === 'uk' ? 'По користувачах' : 'By Users'}
              </button>
              <button
                onClick={() => setStatsViewMode('agentSummary')}
                className={`px-3 py-1.5 rounded transition-all cursor-pointer ${
                  statsViewMode === 'agentSummary'
                    ? 'bg-white text-[#1E3A8A] shadow-xs'
                    : 'text-slate-500 hover:text-slate-800'
                }`}
              >
                {language === 'uk' ? 'По агентах' : 'By Agents'}
              </button>
              <button
                onClick={() => setStatsViewMode('markdown')}
                className={`px-3 py-1.5 rounded transition-all cursor-pointer flex items-center gap-1 ${
                  statsViewMode === 'markdown'
                    ? 'bg-white text-[#1E3A8A] shadow-xs'
                    : 'text-slate-500 hover:text-slate-800'
                }`}
              >
                <FileText className="w-3 h-3 text-purple-600" />
                <span>AGENT_USAGE_STATS.md</span>
              </button>
            </div>

            {/* Search Input */}
            {statsViewMode !== 'markdown' && (
              <div className="relative w-full md:w-64">
                <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder={language === 'uk' ? 'Пошук email або агента...' : 'Search email or agent...'}
                  value={statsSearchQuery}
                  onChange={(e) => setStatsSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-3 py-1.5 text-xs font-mono bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-[#1E3A8A]"
                />
              </div>
            )}
          </div>

          {/* View 1: Detailed User x Agent Table */}
          {statsViewMode === 'userAgent' && (
            <div className="border border-slate-200 rounded-xl overflow-hidden shadow-xs">
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse font-mono text-xs">
                  <thead>
                    <tr className="bg-slate-900 text-white uppercase text-[10px] tracking-wider border-b border-slate-800">
                      <th className="py-3 px-4">{language === 'uk' ? 'Користувач (Email)' : 'User (Email)'}</th>
                      <th className="py-3 px-4">{language === 'uk' ? 'Агент' : 'Agent ID'}</th>
                      <th className="py-3 px-4 text-center">{language === 'uk' ? 'Всього' : 'Total'}</th>
                      <th className="py-3 px-4 text-center">{language === 'uk' ? 'За 30 днів' : 'Last 30 Days'}</th>
                      <th className="py-3 px-4 text-right">{language === 'uk' ? 'Останній запуск' : 'Last Used'}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 bg-white">
                    {isLoadingUsageStats ? (
                      <tr>
                        <td colSpan={5} className="py-8 text-center text-slate-400">
                          <RefreshCw className="w-5 h-5 animate-spin mx-auto mb-2 text-[#1E3A8A]" />
                          <span>{language === 'uk' ? 'Завантаження статистики...' : 'Loading statistics...'}</span>
                        </td>
                      </tr>
                    ) : !usageStatsData?.userAgentStats || usageStatsData.userAgentStats.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="py-8 text-center text-slate-400">
                          {language === 'uk' ? 'Записи про використання агентів відсутні.' : 'No agent usage entries found.'}
                        </td>
                      </tr>
                    ) : (
                      usageStatsData.userAgentStats
                        .filter(row => 
                          row.user_email.toLowerCase().includes(statsSearchQuery.toLowerCase()) ||
                          row.agent_id.toLowerCase().includes(statsSearchQuery.toLowerCase())
                        )
                        .map((row, idx) => {
                          const lastDate = row.last_used_at ? new Date(row.last_used_at).toLocaleString('uk-UA') : '-';
                          return (
                            <tr key={idx} className="hover:bg-slate-50/80 transition-colors">
                              <td className="py-2.5 px-4 font-bold text-slate-800">{row.user_email}</td>
                              <td className="py-2.5 px-4">
                                <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded border border-blue-200 text-[11px] font-bold">
                                  {row.agent_id}
                                </span>
                              </td>
                              <td className="py-2.5 px-4 text-center font-bold text-slate-700">{row.total_count}</td>
                              <td className="py-2.5 px-4 text-center">
                                <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 rounded-full font-bold text-[11px]">
                                  {row.count_30d}
                                </span>
                              </td>
                              <td className="py-2.5 px-4 text-right text-slate-500 text-[11px]">{lastDate}</td>
                            </tr>
                          );
                        })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* View 2: User Summary Table */}
          {statsViewMode === 'userSummary' && (
            <div className="border border-slate-200 rounded-xl overflow-hidden shadow-xs">
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse font-mono text-xs">
                  <thead>
                    <tr className="bg-slate-900 text-white uppercase text-[10px] tracking-wider border-b border-slate-800">
                      <th className="py-3 px-4">{language === 'uk' ? 'Користувач (Email)' : 'User (Email)'}</th>
                      <th className="py-3 px-4 text-center">{language === 'uk' ? 'Всього запусків' : 'Total Runs'}</th>
                      <th className="py-3 px-4 text-center">{language === 'uk' ? 'За останні 30 днів' : 'Last 30 Days'}</th>
                      <th className="py-3 px-4 text-right">{language === 'uk' ? 'Останній запуск' : 'Last Used'}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 bg-white">
                    {!usageStatsData?.userSummary || usageStatsData.userSummary.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="py-8 text-center text-slate-400">
                          {language === 'uk' ? 'Дані відсутні' : 'No data'}
                        </td>
                      </tr>
                    ) : (
                      usageStatsData.userSummary
                        .filter(row => row.user_email.toLowerCase().includes(statsSearchQuery.toLowerCase()))
                        .map((row, idx) => {
                          const lastDate = row.last_used_at ? new Date(row.last_used_at).toLocaleString('uk-UA') : '-';
                          return (
                            <tr key={idx} className="hover:bg-slate-50/80 transition-colors">
                              <td className="py-2.5 px-4 font-bold text-slate-800">{row.user_email}</td>
                              <td className="py-2.5 px-4 text-center font-bold text-slate-700">{row.total_count}</td>
                              <td className="py-2.5 px-4 text-center">
                                <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 rounded-full font-bold text-[11px]">
                                  {row.count_30d}
                                </span>
                              </td>
                              <td className="py-2.5 px-4 text-right text-slate-500 text-[11px]">{lastDate}</td>
                            </tr>
                          );
                        })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* View 3: Agent Summary Table */}
          {statsViewMode === 'agentSummary' && (
            <div className="border border-slate-200 rounded-xl overflow-hidden shadow-xs">
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse font-mono text-xs">
                  <thead>
                    <tr className="bg-slate-900 text-white uppercase text-[10px] tracking-wider border-b border-slate-800">
                      <th className="py-3 px-4">{language === 'uk' ? 'Агент (Agent ID)' : 'Agent ID'}</th>
                      <th className="py-3 px-4 text-center">{language === 'uk' ? 'Всього запусків' : 'Total Runs'}</th>
                      <th className="py-3 px-4 text-center">{language === 'uk' ? 'За останні 30 днів' : 'Last 30 Days'}</th>
                      <th className="py-3 px-4 text-right">{language === 'uk' ? 'Останній запуск' : 'Last Used'}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 bg-white">
                    {!usageStatsData?.agentSummary || usageStatsData.agentSummary.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="py-8 text-center text-slate-400">
                          {language === 'uk' ? 'Дані відсутні' : 'No data'}
                        </td>
                      </tr>
                    ) : (
                      usageStatsData.agentSummary
                        .filter(row => row.agent_id.toLowerCase().includes(statsSearchQuery.toLowerCase()))
                        .map((row, idx) => {
                          const lastDate = row.last_used_at ? new Date(row.last_used_at).toLocaleString('uk-UA') : '-';
                          return (
                            <tr key={idx} className="hover:bg-slate-50/80 transition-colors">
                              <td className="py-2.5 px-4 font-bold text-blue-700">{row.agent_id}</td>
                              <td className="py-2.5 px-4 text-center font-bold text-slate-700">{row.total_count}</td>
                              <td className="py-2.5 px-4 text-center">
                                <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 rounded-full font-bold text-[11px]">
                                  {row.count_30d}
                                </span>
                              </td>
                              <td className="py-2.5 px-4 text-right text-slate-500 text-[11px]">{lastDate}</td>
                            </tr>
                          );
                        })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* View 4: Markdown File Content */}
          {statsViewMode === 'markdown' && (
            <div className="space-y-3">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between text-xs font-mono text-slate-500 bg-slate-100 p-3 rounded-lg border border-slate-200 gap-2">
                <span className="truncate">📁 Шлях: <code className="text-[#1E3A8A] font-bold">{usageStatsData?.markdownFilePath || '/root/deep-research-hub/AGENT_USAGE_STATS.md'}</code></span>
                <button
                  onClick={() => {
                    if (usageStatsData?.markdownContent) {
                      navigator.clipboard.writeText(usageStatsData.markdownContent);
                      alert(language === 'uk' ? 'Зміст .md файлу скопійовано!' : 'Markdown content copied!');
                    }
                  }}
                  className="px-2.5 py-1 bg-white hover:bg-slate-200 border border-slate-300 rounded text-slate-700 font-bold transition-all flex items-center gap-1 cursor-pointer"
                >
                  <Copy className="w-3 h-3 text-slate-600" />
                  <span>{language === 'uk' ? 'Копіювати' : 'Copy'}</span>
                </button>
              </div>

              <div className="bg-slate-900 text-emerald-400 p-5 rounded-xl font-mono text-xs h-96 overflow-y-auto whitespace-pre-wrap leading-relaxed border border-slate-800 shadow-inner">
                {usageStatsData?.markdownContent || (language === 'uk' ? 'Вміст .md файлу ще не створено.' : 'Markdown content empty.')}
              </div>
            </div>
          )}
        </div>
      ) : activeSubTab === 'publicApis' ? (
        <div className="space-y-6 animate-fade-in font-mono">
          <div className="flex items-center justify-between pb-6 border-b border-slate-200 flex-wrap gap-4">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-cyan-50 rounded border border-cyan-200 text-cyan-600">
                <Globe className="w-6 h-6 animate-pulse" />
              </div>
              <div className="space-y-1">
                <h2 className="text-base font-bold uppercase tracking-wider text-slate-800">
                  {language === 'uk' ? '🛰️ МАТРИЦЯ ВІДКРИТИХ ДЖЕРЕЛ ТА ПУБЛІЧНИХ API' : 'PUBLIC APIS & OPEN DATA MATRIX'}
                </h2>
                <p className="text-[11px] text-slate-400">
                  {language === 'uk'
                    ? 'Моніторинг зв\'язку та статусу безкоштовних REST/JSON API (Monobank, НБУ, OpenStreetMap, Open-Meteo, arXiv, USGS)'
                    : 'Real-time connectivity monitoring for free REST/JSON APIs'}
                </p>
              </div>
            </div>
            <button
              onClick={fetchPublicApisHealth}
              disabled={isLoadingPublicApis}
              className="px-4 py-2 bg-cyan-600 hover:bg-cyan-500 text-white text-xs font-bold uppercase rounded-lg transition-all flex items-center gap-2 cursor-pointer disabled:opacity-50 shadow-md"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${isLoadingPublicApis ? 'animate-spin' : ''}`} />
              <span>{isLoadingPublicApis ? (language === 'uk' ? 'Перевірка...' : 'Checking...') : (language === 'uk' ? '⚡ Перевірити зв\'язок' : '⚡ Check Connectivity')}</span>
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {publicApisStatus.length === 0 && !isLoadingPublicApis && (
              <div className="col-span-2 text-center py-12 text-slate-400 text-xs bg-slate-50 border border-slate-200 rounded-xl">
                {language === 'uk' ? 'Натисніть кнопку вище для перевірки статусу підключення публічних API.' : 'Click the button above to test public API connectivity.'}
              </div>
            )}
            {publicApisStatus.map((api: any, idx: number) => (
              <div key={idx} className="bg-slate-50 border border-slate-200 rounded-xl p-5 space-y-3 shadow-xs hover:border-cyan-400 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className={`w-2.5 h-2.5 rounded-full ${api.status === 'online' ? 'bg-emerald-500 animate-pulse' : 'bg-red-500'}`} />
                    <h4 className="text-xs font-extrabold text-slate-800 uppercase tracking-wide">{api.name}</h4>
                  </div>
                  <span className={`px-2.5 py-0.5 text-[9px] font-extrabold uppercase rounded border ${
                    api.status === 'online' ? 'bg-emerald-100 text-emerald-700 border-emerald-300' : 'bg-red-100 text-red-700 border-red-300'
                  }`}>
                    {api.status === 'online' ? 'ONLINE (200 OK)' : 'OFFLINE'}
                  </span>
                </div>
                <div className="text-[11px] text-slate-600 space-y-1.5 pt-1 font-mono">
                  <div className="flex items-center justify-between border-b border-slate-200/60 pb-1">
                    <span className="text-slate-400">Категорія:</span>
                    <span className="font-bold text-slate-700">{api.category}</span>
                  </div>
                  <div className="flex items-center justify-between border-b border-slate-200/60 pb-1">
                    <span className="text-slate-400">Тип авторизації:</span>
                    <span className="font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">{api.authType}</span>
                  </div>
                  <div className="flex items-center justify-between border-b border-slate-200/60 pb-1">
                    <span className="text-slate-400">Затримка відповіді:</span>
                    <span className="font-bold text-slate-800">{api.latencyMs} мс</span>
                  </div>
                  <div className="pt-1 truncate">
                    <span className="text-slate-400 block text-[10px] uppercase">Тестовий Endpoint:</span>
                    <span className="text-blue-600 text-[10px] font-mono truncate block hover:underline">{api.url}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {/* Unlimited Access Configuration Modal */}
      {unlimitedModalUser && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col overflow-hidden animate-fade-in font-mono">
            {/* Modal Header */}
            <div className="p-5 bg-slate-900 text-white flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-500/20 text-amber-400 rounded-lg border border-amber-500/30">
                  <Sparkles className="w-5 h-5 animate-pulse" />
                </div>
                <div>
                  <h3 className="text-sm font-bold uppercase tracking-wider">
                    {language === 'uk' ? 'Налаштування безлімітного доступу' : 'Configure Unlimited Access'}
                  </h3>
                  <p className="text-[11px] text-slate-400 font-sans">{unlimitedModalUser.email}</p>
                </div>
              </div>
              <button 
                onClick={() => setUnlimitedModalUser(null)}
                className="text-slate-400 hover:text-white text-lg font-bold px-2 py-1 cursor-pointer"
              >
                ✕
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 overflow-y-auto space-y-6 flex-1 text-xs">
              {/* Mode selector */}
              <div className="space-y-3">
                <label className="text-[11px] font-bold uppercase text-slate-400 tracking-wider">
                  {language === 'uk' ? 'Режим доступу користувача:' : 'User Access Mode:'}
                </label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {/* Mode 1: None (Standard rules) */}
                  <button
                    type="button"
                    onClick={() => setUnlimitedMode('none')}
                    className={`p-3.5 rounded-xl border text-left cursor-pointer transition-all flex flex-col justify-between ${
                      unlimitedMode === 'none'
                        ? 'border-[#1E3A8A] bg-blue-50/50 ring-2 ring-[#1E3A8A]/20'
                        : 'border-slate-200 bg-white hover:border-slate-300'
                    }`}
                  >
                    <div className="flex items-center justify-between font-bold text-slate-800 text-[11px] mb-1">
                      <span>{language === 'uk' ? 'За загальними правилами' : 'Standard Terms'}</span>
                      <input type="radio" checked={unlimitedMode === 'none'} onChange={() => {}} className="accent-[#1E3A8A]" />
                    </div>
                    <p className="text-[10px] text-slate-500 font-sans leading-normal">
                      {language === 'uk' 
                        ? 'Стандартний доступ за підписками, разовою оплатою або 1 безкоштовним тестовим запитом.'
                        : 'Standard access based on subscriptions, pay-per-run, or free test request.'}
                    </p>
                  </button>

                  {/* Mode 2: All agents */}
                  <button
                    type="button"
                    onClick={() => setUnlimitedMode('all')}
                    className={`p-3.5 rounded-xl border text-left cursor-pointer transition-all flex flex-col justify-between ${
                      unlimitedMode === 'all'
                        ? 'border-purple-600 bg-purple-50/60 ring-2 ring-purple-500/20'
                        : 'border-slate-200 bg-white hover:border-slate-300'
                    }`}
                  >
                    <div className="flex items-center justify-between font-bold text-purple-900 text-[11px] mb-1">
                      <span className="flex items-center gap-1">✨ {language === 'uk' ? 'Повний безліміт' : 'Full Unlimited'}</span>
                      <input type="radio" checked={unlimitedMode === 'all'} onChange={() => {}} className="accent-purple-600" />
                    </div>
                    <p className="text-[10px] text-purple-700/80 font-sans leading-normal">
                      {language === 'uk'
                        ? 'Необмежений безкоштовний доступ до усіх існуючих та майбутніх інструментів.'
                        : 'Unlimited free access to ALL current and upcoming AI agents.'}
                    </p>
                  </button>

                  {/* Mode 3: Selected agents */}
                  <button
                    type="button"
                    onClick={() => setUnlimitedMode('selected')}
                    className={`p-3.5 rounded-xl border text-left cursor-pointer transition-all flex flex-col justify-between ${
                      unlimitedMode === 'selected'
                        ? 'border-amber-600 bg-amber-50/60 ring-2 ring-amber-500/20'
                        : 'border-slate-200 bg-white hover:border-slate-300'
                    }`}
                  >
                    <div className="flex items-center justify-between font-bold text-amber-900 text-[11px] mb-1">
                      <span className="flex items-center gap-1">🎯 {language === 'uk' ? 'Обрані агенти' : 'Selected Agents'}</span>
                      <input type="radio" checked={unlimitedMode === 'selected'} onChange={() => {}} className="accent-amber-600" />
                    </div>
                    <p className="text-[10px] text-amber-700/80 font-sans leading-normal">
                      {language === 'uk'
                        ? 'Безлімітний доступ надається тільки до обраного списку інструментів.'
                        : 'Unlimited access granted only to specific selected AI agents.'}
                    </p>
                  </button>
                </div>
              </div>

              {/* Selected agents list (if mode === 'selected') */}
              {unlimitedMode === 'selected' && (
                <div className="space-y-3 pt-2 border-t border-slate-200">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                    <label className="text-[11px] font-bold uppercase text-slate-600 flex items-center gap-1.5">
                      <span>🎯 {language === 'uk' ? 'Виберіть агентів для безліміту:' : 'Select agents for unlimited access:'}</span>
                      <span className="text-amber-600 font-extrabold">({selectedAgents.length})</span>
                    </label>

                    <div className="flex items-center gap-2 text-[10px]">
                      <button
                        type="button"
                        onClick={() => setSelectedAgents(agentsConfig.map(a => a.id))}
                        className="px-2 py-1 bg-amber-100 text-amber-800 rounded hover:bg-amber-200 font-bold transition-all cursor-pointer"
                      >
                        {language === 'uk' ? 'Обрати всі' : 'Select All'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setSelectedAgents([])}
                        className="px-2 py-1 bg-slate-100 text-slate-600 rounded hover:bg-slate-200 font-bold transition-all cursor-pointer"
                      >
                        {language === 'uk' ? 'Зняти всі' : 'Clear All'}
                      </button>
                    </div>
                  </div>

                  {/* Filter input */}
                  <input
                    type="text"
                    placeholder={language === 'uk' ? "Фільтр агентів за назвою..." : "Filter agents..."}
                    value={agentSearchQuery}
                    onChange={(e) => setAgentSearchQuery(e.target.value)}
                    className="w-full bg-slate-50 border border-slate-200 rounded px-3 py-1.5 text-xs text-slate-800 font-mono focus:outline-none focus:border-amber-500"
                  />

                  {/* List of agents */}
                  <div className="max-h-60 overflow-y-auto border border-slate-200 rounded-xl divide-y divide-slate-100 bg-slate-50/50 p-1">
                    {agentsConfig
                      .filter(a => 
                        a.nameUk.toLowerCase().includes(agentSearchQuery.toLowerCase()) || 
                        a.nameEn.toLowerCase().includes(agentSearchQuery.toLowerCase()) ||
                        a.id.toLowerCase().includes(agentSearchQuery.toLowerCase())
                      )
                      .map(agent => {
                        const isChecked = selectedAgents.includes(agent.id);
                        return (
                          <label
                            key={agent.id}
                            className={`flex items-center justify-between p-2.5 rounded-lg cursor-pointer transition-colors ${
                              isChecked ? 'bg-amber-50/80 border border-amber-200/60' : 'hover:bg-white'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedAgents(prev => [...prev, agent.id]);
                                  } else {
                                    setSelectedAgents(prev => prev.filter(id => id !== agent.id));
                                  }
                                }}
                                className="w-4 h-4 text-amber-600 rounded border-slate-300 focus:ring-amber-500 cursor-pointer"
                              />
                              <div>
                                <p className="font-bold text-slate-800 text-[11px]">
                                  {language === 'uk' ? agent.nameUk : agent.nameEn}
                                </p>
                                <p className="text-[9px] text-slate-400">
                                  {language === 'uk' ? agent.categoryUk : agent.categoryEn} • ID: {agent.id}
                                </p>
                              </div>
                            </div>

                            <span className="text-[9px] px-1.5 py-0.5 rounded font-bold uppercase bg-purple-100 text-purple-700">
                              ✨ Gemini 3.7 Flash
                            </span>
                          </label>
                        );
                      })}
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="p-4 bg-slate-50 border-t border-slate-200 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setUnlimitedModalUser(null)}
                className="px-4 py-2 bg-slate-200 hover:bg-slate-300 text-slate-700 font-bold rounded-lg text-xs uppercase cursor-pointer transition-all"
              >
                {language === 'uk' ? 'Скасувати' : 'Cancel'}
              </button>
              <button
                type="button"
                onClick={handleSaveUnlimited}
                disabled={isSavingUnlimited}
                className="px-5 py-2 bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 text-white font-bold rounded-lg text-xs uppercase shadow-md cursor-pointer transition-all flex items-center gap-1.5 disabled:opacity-50"
              >
                {isSavingUnlimited ? (
                  <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Sparkles className="w-3.5 h-3.5 text-amber-400" />
                )}
                <span>{language === 'uk' ? 'Зберегти безліміт' : 'Save Unlimited'}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
